/**
 * FoyerBridge - the one seam between the Foyer web app and whatever device it
 * runs on.
 *
 * The same web app runs on several TV platforms, each with its own way of doing
 * the things only the device can do (launch an app, later: tune a channel,
 * power, volume). Instead of sprinkling `if (tizen) … else if (philips) …`
 * across the schemes, the app talks to ONE bridge and branches on *capability*,
 * never on platform: it asks "can you launch apps?" and hides the tile if not.
 *
 * Each platform provides its own implementation, picked by feature-detection:
 *   - Android (STB + Philips MediaSuite): a `window.FoyerNative` object that the
 *     native WebView shell injects. The MediaSuite-vs-STB difference (JEDI
 *     `IApplicationControl` vs a plain intent) lives entirely on the Kotlin side
 *     behind that object - the web layer can't tell them apart and doesn't try.
 *   - Samsung Tizen / LG webOS: pure-JS adapters calling the platform globals
 *     the OS injects (`window.tizen` / `window.webOS`). No native shell - on
 *     those platforms the Foyer app *is* the web app.
 *   - plain browser / CMS preview: no device - every capability is false.
 *
 * Capabilities are deliberately uneven across platforms; a platform that can't
 * do something reports `false` and the UI degrades uniformly, with zero
 * platform-specific code.
 */

import { guessModel } from "./binding";
import { expectForegroundLoss } from "./foregroundWatch";
import {
  resolveLaunchTarget,
  launchIdFor as sharedLaunchIdFor,
  type LaunchPlatform,
  type LaunchableFields,
} from "@shared/app-resolve";
import type { DevicePlatform, DeviceCapability } from "@shared/types/foyer";

/**
 * Where the web app is running. Structurally the same set as the shared
 * {@link LaunchPlatform} - aliased rather than redeclared so the two can never
 * drift apart.
 */
export type TvPlatform = LaunchPlatform;

/** The fields a launch needs - structurally satisfied by ResolvedApp. */
export interface LaunchableApp extends LaunchableFields {
  name?: string;
  androidSlug?: string;
  tizenId?: string;
  webosId?: string;
  /** Apple iOS / tvOS custom URL scheme (e.g. "nflx://"), shared by both units. */
  iosScheme?: string;
  /** https web address (e.g. "https://www.netflix.com/"). Last-resort target
   *  when the unit carries no native id for this app - see `dispatchLaunch`. */
  webUrl?: string;
}

/**
 * Authoritative device identity from the native shell. On Philips MediaSuite
 * the Kotlin side reads this from the JEDI product-info API (the same SDK that
 * provides `IApplicationControl` for launch); other shells fill what they can.
 * Every field is optional - a shell that only knows the model still helps.
 * This is the *exact* model (e.g. "43HFL5114T/12"), unlike the UA-derived
 * `guessModel()` which can never recover the commercial type number.
 */
export interface DeviceInfo {
  /** Commercial model / type number, e.g. "43HFL5114T/12". */
  model?: string;
  /** Firmware / software version reported by the platform. */
  firmware?: string;
  /** Hardware serial number. */
  serial?: string;
}

/**
 * Live network state from the native shell. Volatile (unlike {@link DeviceInfo}),
 * so it's read on every heartbeat rather than cached. Every field is optional -
 * a TV on Ethernet has no `wifi*`; a TV on Wi-Fi has no `ethIp`; the OS may
 * withhold the SSID without a location grant.
 */
export interface NetworkInfo {
  /** IPv4 on the Wi-Fi interface (wlan0). */
  wifiIp?: string;
  /** IPv4 on the wired interface (eth0). */
  ethIp?: string;
  /** Wi-Fi signal strength in dBm (e.g. -55); closer to 0 is stronger. */
  wifiSignal?: number;
}

/**
 * An external input the TV can switch to (HDMI, AV, …). Enumerated from the
 * device so the picker shows exactly the ports that physically exist.
 */
export interface InputSource {
  /** Platform id passed back to `setInput` (e.g. "HDMI1", or a Tizen index). */
  id: string;
  /** Display name, e.g. "HDMI 1". */
  label: string;
  /** Whether a device is detected on the port, when the platform reports it. */
  connected?: boolean;
}

/** Credentials for the TV's own guest hotspot (the SoftAP a guest joins). */
export interface GuestHotspotCredentials {
  /** The SSID the TV broadcasts. */
  ssid: string;
  /** The hotspot password (WPA). */
  password: string;
}

/**
 * Guest-casting state from the native shell. On Philips MediaSuite the firmware
 * implements both the guest hotspot (SoftAP) and the Chromecast receiver; the
 * shell only enables/configures them via JEDI. `supported` is false on anything
 * that isn't a MediaSuite TV (a plain STB, web), which keeps the real hotspot UI
 * hidden there.
 */
export interface GuestCastingStatus {
  /** Whether this device can do guest casting at all (MediaSuite via JEDI). */
  supported: boolean;
  /** Whether the guest hotspot is currently broadcasting. */
  hotspotEnabled: boolean;
  /** Whether the Chromecast receiver is currently enabled. */
  castEnabled: boolean;
  /** The SSID currently configured on the hotspot, when known. */
  ssid?: string;
  /** The password currently configured on the hotspot, when known. */
  password?: string;
}

/**
 * The device's own screen-sharing surface, as resolved by the native shell
 * (`ScreenShareProvider` in the Android kiosk).
 *
 * The product rule this exists for: Foyer never draws a casting UI when the
 * device already has one. A Philips TV has its JEDI Cast app, an Android TV box
 * has its OEM mirroring app, a Google TV box has Chromecast built-in with no UI
 * at all - only that last case falls back to our instruction page.
 */
export interface ScreenShareTarget {
  /** Whether this device can receive a screen share at all. */
  available: boolean;
  /** Whether there is something we can OPEN (false for Chromecast built-in). */
  launchable: boolean;
  /** How it opens: "jedi" | "app" | "settings" | "receiver" (nothing to open). */
  kind?: string;
  /** Package name, settings action, or JEDI app name - diagnostics only. */
  id?: string;
  /** The receiver's own display name, e.g. "Screen mirroring". */
  label?: string;
  /** What the guest's phone speaks: "chromecast" | "miracast". */
  protocol?: string;
  /**
   * What this device calls ITSELF in a phone's cast list (its Google Cast /
   * Android device name). The name the guest must actually pick - which is not
   * the room label our own chrome prints.
   */
  deviceName?: string;
  /**
   * Every package the native resolution considered, with a note when one matched
   * but exposes no way in. Diagnostics only - but it is what turns "this device
   * has no screen sharing" from a verdict into something checkable.
   */
  candidates?: string[];
}

/**
 * How a guest gets their content onto this TV. Deliberately protocol-level, not
 * brand-level: the Screen-Share page renders instructions from these, so the
 * copy is right without the page knowing which platform it's on.
 *   - "chromecast"  - a Google Cast receiver ("Cast" on Android/Chrome),
 *   - "airplay"     - an AirPlay receiver (iPhone/Mac "Screen Mirroring"),
 *   - "miracast"    - Wi-Fi Direct screen mirroring (Windows "Project",
 *                     Samsung "Smart View", Android "Wireless display"),
 *   - "dial"        - DIAL app-launch (YouTube/Netflix "cast to TV" that opens
 *                     the TV's own app rather than mirroring).
 */
export type CastProtocol = "chromecast" | "airplay" | "miracast" | "dial";

/**
 * How the guest reaches the receiver:
 *   - "wifi-direct" - the receiver makes its own peer-to-peer group and shows
 *     its own on-screen code; the guest picks the TV from their device's cast
 *     list. Nothing to join first (Philips MediaSuite's current model).
 *   - "hotspot"     - the TV broadcasts a SoftAP the guest joins, then casts
 *     over it. Credentials come from {@link GuestCastingStatus}.
 *   - "lan"         - receiver and guest share the house network; discovery is
 *     ordinary mDNS/SSDP. Nothing for us to bring up.
 */
export type CastTransport = "wifi-direct" | "hotspot" | "lan";

/**
 * The normalized casting model - ONE shape the UI renders from, on every
 * platform. Philips MediaSuite fills it from JEDI `ISharingSettings`, Tizen and
 * webOS from their own guarded platform globals, and anything else reports
 * `supported: false` so the page falls back to generic instructions.
 *
 * This is the formalization the Screen-Share page consumes; the older
 * {@link GuestCastingStatus} remains the Android/JEDI *wire* shape underneath.
 */
export interface CastingInfo {
  /** Whether this device can receive a guest cast at all. */
  supported: boolean;
  /** Whether the receiver is currently enabled/advertising. */
  enabled: boolean;
  /** What the receiver speaks. Empty when `supported` is false. */
  protocols: CastProtocol[];
  /** How the guest's device reaches it. */
  transport: CastTransport;
  /**
   * Whether {@link FoyerBridge.launchCastReceiver} does something here. On
   * Wi-Fi Direct the receiver IS the casting UI and must be launched when the
   * guest opens Screen Share; on a LAN receiver there is nothing to pop.
   */
  launchable: boolean;
  /**
   * What the device calls itself in the guest's cast list, when it knows. The
   * instruction page names THIS rather than the room, because that is the entry
   * the guest is looking for.
   */
  deviceName?: string;
  /** Hotspot SSID, when `transport` is "hotspot" and it's known. */
  ssid?: string;
  /** Hotspot password, when `transport` is "hotspot" and it's known. */
  password?: string;
}

/**
 * What the current device can do. Grows as we add channel tuning, power, etc.;
 * the app reads this to decide what to render, never the platform name.
 */
export interface BridgeCapabilities {
  /** Can launch installed apps (a non-web platform with a launch mechanism). */
  launchApp: boolean;
  /**
   * Can list + switch external inputs (a real TV: Philips MediaSuite via JEDI,
   * or Samsung Tizen via its B2B API). False on the Android STB (it has no
   * display inputs of its own, and its shell doesn't expose input control) and
   * on plain web - the HDMI picker hides there.
   */
  switchInput: boolean;
  /**
   * Can broadcast a guest hotspot + run the Chromecast receiver (Philips
   * MediaSuite via JEDI `ISharingSettings`). False on the Android STB (the shell
   * exposes the methods but the firmware reports unsupported) and on plain web -
   * the real Screen-Share QR hides there and an instructional page shows instead.
   */
  guestCasting: boolean;
  /**
   * Can ask the firmware to run its own checkout guest-data clear - the thing
   * that actually removes native app logins (Netflix, YouTube…) that live
   * outside our sandbox. See {@link FoyerBridge.clearGuestCredentials}.
   *
   * True ONLY where the platform positively declares the feature: Philips
   * MediaSuite whose JEDI PMS surface lists `ROOM_STATUS_CHECK_OUT`, or a
   * Samsung/LG hospitality firmware that exposes a clear entry point. False
   * everywhere else, including devices where the method exists but the firmware
   * would silently no-op - a wipe we cannot confirm must never be advertised.
   */
  clearGuestCredentials: boolean;
  /**
   * Can tell the firmware a guest is in-house, so it retains their app logins
   * for the stay. Reported separately from {@link clearGuestCredentials}
   * because a firmware may declare one PMS room-status feature without the
   * other - each is advertised on its own evidence.
   */
  guestCheckIn: boolean;
  /**
   * Can this room actually carry a video call - camera enumerated AND capture
   * granted, not merely "the shell has the methods". False on every box with
   * nothing plugged into the USB port, which is the same shared-APK problem
   * {@link BridgeCapabilities.switchInput} and `powerControl` have. See
   * {@link canVideoCall}.
   */
  videoCall: boolean;
}

/**
 * Why a device can (or can't) carry a video call - the answer to support's
 * first question, "is the camera even enumerated?". See
 * {@link FoyerNativeBridge.videoCallMode}.
 */
export type VideoCallMode = "camera2" | "no-camera" | "no-permission" | "none";

/**
 * How a device is managed - the privilege behind silent camera grants, silent
 * APK installs and a real reboot.
 *
 *  - `"device-owner"` - enrolled at staging (`dpm set-device-owner`). The shell
 *    grants its own CAMERA/RECORD_AUDIO at boot, installs OTA APKs unattended
 *    and can reboot the box. This is the shape a room should ship in.
 *  - `"profile-owner"` - a work-profile enrolment. Possible on a tablet, useless
 *    for a TV: reported rather than folded into `"none"` so a mis-provisioned
 *    unit is visible instead of merely absent from the managed list.
 *  - `"none"` - hand-held. Whatever adb granted it once, on a bench, with a
 *    laptop plugged in. Everything still works EXCEPT unattended updates and
 *    remote recovery.
 *
 * Device owner can only be set on a device with no accounts (factory-fresh or
 * just reset), so this cannot be fixed remotely on a box already in a room.
 * See `docs/device-owner-provisioning.md`.
 */
export type ManagementMode = "device-owner" | "profile-owner" | "none";

/**
 * Outcome of a firmware guest-data clear. Deliberately not a bare boolean: this
 * is acked back to the server and lands in the property's audit trail, so it has
 * to say WHICH mechanism ran (or why none did) rather than just "false".
 */
export interface PlatformSignalResult {
  /** Did the platform confirm it accepted the signal? */
  ok: boolean;
  /** Mechanism used ("jedi-pms", a Tizen/webOS entry-point name, or "none"). */
  method: string;
  /** Human-readable detail - the reason when it didn't. */
  detail: string;
  platform: TvPlatform;
}

export interface CredentialWipeResult {
  /** Did the platform confirm it ran its guest-data clear? */
  cleared: boolean;
  /** Mechanism used: "jedi-pms", a Tizen/webOS entry-point name, or "none". */
  method: string;
  /** Human-readable detail - the reason on failure. Never a secret. */
  detail: string;
  /** Platform the attempt ran on, so fleet-wide reports can group by it. */
  platform: TvPlatform;
}

export interface FoyerBridge {
  /** Where we're running. Prefer `capabilities()` over reading this directly. */
  readonly platform: TvPlatform;
  /** What this device can do right now. */
  capabilities(): BridgeCapabilities;
  /**
   * Native device identity (Philips: from JEDI), or `null` when the shell
   * doesn't provide it - plain web, CMS preview, or a shell that hasn't wired
   * it yet. Callers should fall back to `guessModel()` (see `deviceModel()`).
   */
  getDeviceInfo(): DeviceInfo | null;
  /**
   * The installed native app version (e.g. "0.2.2"), or `null` on shells that
   * don't report one (plain web / CMS preview). On web the caller falls back to
   * the build-time `VITE_APP_VERSION`.
   */
  appVersion(): string | null;
  /**
   * The installed version of the streaming app with the given package slug (the
   * "TV" tile target), or `null` when the shell doesn't report it (web/CMS
   * preview) or it isn't installed. The slug is CMS-configurable; pass the value
   * from config. Reported for fleet visibility of the tightly-coupled ecosystem.
   */
  streamingAppVersion(packageSlug?: string): string | null;
  /**
   * Live network state (IPs, Wi-Fi SSID/signal), or `null` when the shell
   * doesn't provide it. Read fresh each heartbeat - values change as the TV
   * roams between Wi-Fi and Ethernet.
   */
  getNetworkInfo(): NetworkInfo | null;
  /**
   * Open `app` on the current platform. Returns true if a launch was
   * dispatched, false if there's no way to launch here (web, missing id for the
   * platform, or the native call threw). Never throws - a bad launch must not
   * crash the screen.
   */
  launchApp(app: LaunchableApp): boolean;
  /**
   * The external inputs physically present on this TV (HDMI 1..n, AV, …), or
   * `[]` when the platform can't enumerate them (Android STB / web). On Tizen
   * the OS answers asynchronously, so the first call may return the cached
   * (possibly empty) list and populate shortly after - callers should re-read.
   */
  listInputs(): InputSource[];
  /**
   * Switch the TV to the input with the given id. Returns true if the switch was
   * dispatched, false if unsupported or the native call threw. Never throws.
   */
  setInput(id: string): boolean;
  /**
   * Current guest-casting state (hotspot + Chromecast), or `null` when the
   * platform can't report it (Android STB / web). Read fresh - the firmware is
   * the source of truth for what's actually broadcasting.
   *
   * This is the raw Android/JEDI shape. UI should prefer {@link getCastingInfo},
   * which normalizes it alongside the Tizen and webOS receivers.
   */
  getGuestCastingStatus(): GuestCastingStatus | null;
  /**
   * What the native shell resolved as this device's own screen-sharing surface,
   * or `null` off a shell that can answer. Diagnostics: {@link getCastingInfo}
   * is what the UI renders from, but when Screen Share falls back to the
   * instruction page this is the only thing that says WHY - which package was
   * picked, or that nothing was found. Never throws.
   */
  getScreenShareTarget(): ScreenShareTarget | null;
  /**
   * The normalized casting model for this device - the shape the Screen-Share
   * page renders from, on every platform. Never null: a device that can't cast
   * reports `supported: false` (with `protocols: []`), which is exactly the
   * signal to show the generic instructional fallback. Never throws.
   */
  getCastingInfo(): CastingInfo;
  /**
   * Bring up the guest hotspot with `creds` and enable the Chromecast receiver on
   * it. Returns true if the request was dispatched, false if unsupported or the
   * native call threw. Never throws.
   */
  enableGuestCasting(creds: GuestHotspotCredentials): boolean;
  /**
   * Open the device's OWN built-in screen-sharing receiver - the Philips Cast
   * app on a MediaSuite TV, the OEM mirroring app on an Android TV box. This is
   * what the Screen Share tile does: the built-in receiver shows the real device
   * name / PIN / QR that phones actually discover, which our own page never
   * could. Returns true when a receiver was dispatched, false when this device
   * has none to open (the caller then shows the instruction page). Never throws.
   */
  launchCastReceiver(): boolean;
  /**
   * Turn the guest hotspot and Chromecast back off. Returns true if dispatched,
   * false if unsupported or the native call threw. Never throws.
   */
  disableGuestCasting(): boolean;
  /**
   * Apply a native APK self-update (Phase 4 OTA, apk kind). Returns true if the
   * native updater was invoked, false if no updater is present (the web-bundle
   * OTA path covers those devices). Never throws.
   */
  applyUpdate(url: string): boolean;
  /**
   * Trigger an OEM firmware update (Phase 4 OTA, firmware kind) by handing the
   * TV's own updater the image URL + metadata. Returns true if the native
   * trigger was invoked, false if this device can't update firmware from
   * software (then it never reported `otaFirmware`). Never throws.
   */
  applyFirmwareUpdate(args: {
    url: string;
    version: string;
    fileName: string;
    sizeBytes: number;
  }): boolean;
  /**
   * Restore an OEM clone file (TEP cloning, Samsung `webapis.clone`).
   * Downloads `url` into the widget's own storage and hands the LOCAL path to
   * `restoreCloneData` (which takes a path, never a URL). Resolves with the
   * verdict; never rejects. `applied:false` everywhere without `cloneRestore`.
   */
  restoreClone(url: string, folder?: string): Promise<CloneRestoreResult>;
  /**
   * Set the display power (Phase 5 - scheduled on/off). Returns true ONLY when
   * the panel actually changed state - a native shell that can't control power
   * (or whose firmware refused) returns false, and so does every non-native
   * platform. The caller must treat `false` as "the display did NOT change":
   * the energy/ESG report is built from confirmed states, so a fire-and-forget
   * "we asked" reading here would manufacture savings that never happened.
   * Never throws.
   */
  setPower(on: boolean): boolean;
  /**
   * Set the device's NTP (time) server (OTA add-on - `set_ntp`). Returns true if
   * the native call was dispatched, false where the shell can't configure NTP
   * (then the device never reported `setNtp`). Never throws.
   */
  setNtpServer(server: string): boolean;
  /**
   * The installer's escape hatch onto native Philips Professional Settings
   * (CDB URL, room number, network, ...): suspends the Android kiosk shell's
   * return-guard for several minutes so it stops reclaiming the screen the
   * instant the remote's unlock combo backgrounds Foyer. Everywhere else
   * (Tizen, web, a plain STB) this is a no-op returning false - there is no
   * equivalent kiosk-reclaim behaviour to suspend. Returns true only when the
   * native shell actually dispatched it; never throws.
   */
  unlockKioskForProSettings(): boolean;
  /**
   * Ask the native shell to play one DASH manifest directly through
   * ExoPlayer, bypassing Shaka/the WebView's own EME stack - the fallback
   * for content Shaka cannot license itself. PLAYER-6, hasselbacken/301,
   * 2026-09-08: a live Alcom channel failed Shaka's EME negotiation with
   * `REQUESTED_KEY_SYSTEM_CONFIG_UNAVAILABLE` on this Android WebView build
   * even with a confirmed-correct, non-empty `drm.servers` config - not a
   * missing licence, not an absent CDM, not a stale-closure race, all ruled
   * out on real hardware - while the exact same manifest+licence plays
   * through this panel's own native video app via ExoPlayer's direct
   * `MediaDrm` access. `licenceUrl` empty/omitted means clear content.
   *
   * Returns true only once native playback has been ASKED for - never
   * whether it succeeds, which is unavoidably async on every player, native
   * or web, and arrives later via `window.addEventListener
   * ("foyer:native-playback-ready" | "foyer:native-playback-error", ...)`.
   * False (absent shell method, or any dispatch failure) means the caller
   * keeps its own (Shaka) error handling - this is a fallback, never a
   * requirement. Never throws.
   */
  startNativePlayback(url: string, licenceUrl?: string | null): boolean;
  /**
   * Stop and release whatever `startNativePlayback` started, and hide its
   * surface. Idempotent - a no-op with nothing playing. Never throws.
   */
  stopNativePlayback(): boolean;
  /**
   * How this box is managed (see {@link ManagementMode}). `"device-owner"` is
   * the provisioned shape every room should ship in; `"none"` means the unit is
   * hand-held - whatever adb granted it on a bench, once, with a laptop plugged
   * in. Reported in the heartbeat so the fleet can tell those two apart before a
   * rollout stalls on a dialog nobody in the room can press. Never throws.
   */
  managementMode(): ManagementMode;
  /**
   * Reboot the DEVICE. Returns true only when the OS accepted a real reboot -
   * which needs device-owner privilege. `false` means the caller must fall back
   * to `location.reload()`, i.e. the page restarting itself, which cannot
   * recover a wedged WebView (that is the whole reason this exists). Never
   * throws.
   */
  rebootDevice(): boolean;
  /**
   * Ask the platform to run its own checkout guest-data clear - the only
   * mechanism that can remove native app logins (Netflix, YouTube, Spotify…),
   * which live in the OS's per-app sandboxes where no app of ours can reach.
   *
   * We are the TRIGGER, not the eraser. Every hospitality platform owns this
   * clear and exposes a hook for the integrator to pull at check-out:
   *   - Philips MediaSuite → JEDI `IPMS.checkOutSuccessful()` (via the shell),
   *   - Samsung Tizen      → a B2B hospitality entry point, feature-detected,
   *   - LG webOS           → an HCAP entry point, feature-detected.
   *
   * Async because the Tizen/webOS surfaces are callback-based. NEVER rejects:
   * a failure resolves with `cleared: false` and a reason, because a checkout
   * must not be derailed by a firmware that won't co-operate - and because the
   * result is audited, an unconfirmed clear has to be recorded as unconfirmed.
   */
  clearGuestCredentials(): Promise<CredentialWipeResult>;
  /**
   * Tell the platform a guest has checked IN - the other half of checkout
   * hygiene, and the reason it works at all.
   *
   * Hospitality firmware clears OTT credentials aggressively (LG documents
   * clearing them at every power-off) unless a PMS integration says the room is
   * occupied. Without this a guest re-authenticates every morning, and our
   * checkout wipe has nothing left to clear. Same feature-detection and
   * never-rejects contract as {@link clearGuestCredentials}.
   */
  signalGuestCheckIn(): Promise<PlatformSignalResult>;
  /**
   * Why this device can or can't carry a video call, or `null` where nothing can
   * answer (plain web, CMS preview, a shell that predates video calling). Note
   * `null` ≠ `"none"`: one means "we don't know", the other means the shell
   * looked and found no camera stack - support needs to tell those apart.
   * Never throws.
   */
  videoCallMode(): VideoCallMode | null;
  /**
   * Arm the native capture gate BEFORE requesting media, at the moment a call
   * reaches `active` (VC-13, design §4.2) - never while ringing, never
   * speculatively. Returns whether the OS already holds camera/mic permission:
   * `true` means `getUserMedia` can proceed now; `false` means a runtime grant
   * was just requested and the caller should await the `foyer:media-permissions`
   * event. On a non-native platform (plain web / CMS preview) there is no gate
   * and the browser handles permission itself, so this returns `true`. Never
   * throws.
   */
  startCallSession(): boolean;
  /**
   * Disarm the capture gate on EVERY exit from a call - ended, declined, timed
   * out, error, and component unmount - so no camera light survives the call
   * (VC-20 acceptance). Idempotent, a no-op on non-native platforms, never
   * throws.
   */
  endCallSession(): void;
  /**
   * Whether the native capture gate is currently open. `false` on non-native
   * platforms. Never throws.
   */
  isCallSessionActive(): boolean;
  /**
   * The device's push-wake (FCM) registration token (VC-28), or `null` when the
   * shell has no push channel - plain web, CMS preview, or an Android build with
   * Firebase not configured (no `google-services.json`), where the shell is
   * present but never registered. The TV reports this in its heartbeat so the
   * server can wake and foreground Foyer for an inbound call while the WebView
   * is backgrounded. Never throws.
   */
  getFcmToken(): string | null;
}

interface TizenApplicationManager {
  launch(
    id: string,
    onSuccess?: () => void,
    onError?: (e: unknown) => void
  ): void;
}
interface WebOSService {
  request(
    uri: string,
    options: {
      method?: string;
      parameters?: Record<string, unknown>;
      onSuccess?: (res: unknown) => void;
      onFailure?: (err: unknown) => void;
    }
  ): void;
}
interface FoyerNativeBridge {
  launchApp(id: string): void;
  /**
   * Optional finer-grained platform tag. Exposed by the native shell as a
   * @JavascriptInterface method (so it's a function on the JS side, not a
   * string property): "android-mediasuite" on a Philips MediaSuite TV vs
   * "android-stb" on a generic box. Absent on shells that predate it.
   */
  platform?(): string | null | undefined;
  /**
   * Optional device identity. Returns a {@link DeviceInfo} object, or a JSON
   * string of one - Android `@JavascriptInterface` methods can only return
   * strings, so the shell typically returns JSON and we parse it. Absent on
   * shells that haven't implemented it; must never throw.
   */
  getDeviceInfo?(): DeviceInfo | string | null | undefined;
  /** Optional installed app version string (e.g. "0.2.2"). */
  appVersion?(): string | null | undefined;
  /**
   * Optional installed version of the streaming app at `packageName` (the "TV"
   * tile target). The package is passed in (CMS-configurable slug). Empty/absent
   * when it isn't installed or the shell predates it.
   */
  streamingAppVersion?(packageName: string): string | null | undefined;
  /**
   * Optional durable key/value store (native SharedPreferences). Used to persist
   * the pairing binding so it survives a reboot or a WebView-storage wipe, which
   * plain localStorage does not guarantee on hotel-TV firmware. Absent on shells
   * that haven't implemented it; callers fall back to localStorage.
   */
  storeGet?(key: string): string | null | undefined;
  storeSet?(key: string, value: string): void;
  storeRemove?(key: string): void;
  /**
   * Optional live network info. Like {@link getDeviceInfo}, returns an object
   * or a JSON string of one (Android `@JavascriptInterface` returns strings).
   * Absent on shells that haven't implemented it; must never throw.
   */
  getNetworkInfo?(): NetworkInfo | string | null | undefined;
  /**
   * Optional external-input control. Philips MediaSuite implements these via
   * JEDI (the same SDK behind app launch); the plain Android STB shell does
   * NOT, which is exactly how the input picker stays hidden on the box.
   * `listInputs` returns an {@link InputSource}[] or a JSON string of one.
   */
  listInputs?(): InputSource[] | string | null | undefined;
  setInput?(id: string): void;
  /**
   * Optional guest-casting control. Philips MediaSuite implements these via JEDI
   * `ISharingSettings` (the firmware owns the SoftAP + Chromecast; the shell only
   * enables/configures them). `guestCastingStatus` returns a {@link
   * GuestCastingStatus} or a JSON string of one; on a plain STB it reports
   * `supported:false`, which is how the real hotspot UI stays hidden off
   * MediaSuite. Must never throw.
   */
  guestCastingStatus?(): GuestCastingStatus | string | null | undefined;
  enableGuestCasting?(ssid: string, password: string): void;
  launchCastReceiver?(): void;
  disableGuestCasting?(): void;
  /**
   * Optional: the device's OWN screen-sharing surface, resolved natively -
   * a {@link ScreenShareTarget} or a JSON string of one. Absent on shells older
   * than the resolver (they only had the MediaSuite/JEDI path), which is why
   * every read of it must degrade to the JEDI answer rather than assume nothing
   * is there. Must never throw.
   */
  screenShareTarget?(): ScreenShareTarget | string | null | undefined;
  /**
   * Which framebuffer route the shell has - `"accessibility"` (a bench-enabled
   * capture service, API 30+), `"screencap"` (a privileged/OEM-signed build whose
   * SurfaceFlinger answers) or `"none"`. Keyed off THIS probe rather than the
   * presence of {@link requestScreenshot}, for the usual reason: the same APK runs
   * on an Android-Q MediaSuite TV and an un-provisioned STB, where the method
   * exists and can capture nothing. Absent on shells that predate native capture -
   * read as `"none"`. Must never throw.
   */
  screenshotMode?(): string | null | undefined;
  /**
   * Start a native framebuffer capture, identified by `requestId`. Returns
   * immediately; the shell dispatches a `foyer:screenshot` DOM event
   * (`{id, ok, mode}`) when the grab finishes, and the bytes are then collected
   * with {@link takeScreenshotResult}.
   *
   * Deliberately not synchronous: a framebuffer composite / a `screencap` fork on
   * the page's JS thread would freeze the UI for the duration, on the very box
   * whose display stack may be the fault being diagnosed. Must never throw.
   */
  requestScreenshot?(requestId: string): void;
  /**
   * Collect the finished capture for `requestId` as a `data:` URL (`image/png` or
   * `image/jpeg`), or `""` when there is nothing to collect. Consuming - a second
   * read returns `""`. Must never throw.
   */
  takeScreenshotResult?(requestId: string): string | null | undefined;
  /**
   * Whether the shell has a native log tail - `"logcat"` or `"none"`. Absent on
   * shells that predate it. Must never throw.
   */
  logCaptureMode?(): string | null | undefined;
  /**
   * Start a native log capture. Same request/notify/collect handshake as
   * {@link requestScreenshot}: fires `foyer:logs` and the text is collected with
   * {@link takeLogsResult}. Must never throw.
   */
  requestLogs?(requestId: string): void;
  /** Collect the finished log capture for `requestId`, or `""`. Consuming. */
  takeLogsResult?(requestId: string): string | null | undefined;
  /**
   * Recent process exits (API 30+) and WebView renderer deaths, as a JSON
   * array (kiosk 0.2.49+). Posted on boot by `nativeExits.ts`. Absent on older
   * shells, Tizen and the browser. Must never throw.
   */
  exitEvents?(): string | null | undefined;
  /**
   * LEGACY synchronous native capture: a base64 PNG (with or without a data-URL
   * prefix) / a log tail, returned inline.
   *
   * Declared since the diagnostics increment and implemented by no shell that ever
   * shipped - the Android shell uses the async handshake above instead. Still
   * honoured when present so a third-party or future shell can take the simple
   * route, but do NOT add a synchronous capture to our own shell: see
   * {@link requestScreenshot}. Must never throw.
   */
  captureScreenshot?(): string | null | undefined;
  captureLogs?(): string | null | undefined;
  /**
   * Optional native APK self-update (Phase 4 OTA). Hands the shell a download
   * URL for a new build to install (PackageInstaller; JEDI silent-install where
   * privileged, else a user prompt). Absent on shells that can't self-update -
   * the web-bundle OTA path (a hard reload) covers those. Must never throw.
   */
  applyUpdate?(url: string): void;
  /**
   * How the shell installs that APK - `"silent"` (privileged / device-owner, no
   * on-screen confirmation), `"prompt"` (the system installer dialog: a human
   * must confirm in the room) or `"none"` (no usable installer). Absent on older
   * shells that only expose `applyUpdate`; those are treated as `"prompt"`, the
   * conservative reading. Must never throw.
   */
  otaInstallMode?(): string | null | undefined;
  /**
   * Optional native OEM firmware update (Phase 4 OTA, firmware kind). Hands the
   * TV's *own* updater the OEM image URL + metadata to download and flash:
   * Philips MediaSuite → JEDI `startCloneInFromServerToTV(MAIN_SOFTWARE)`;
   * Samsung Tizen →
   * `webapis.systemcontrol.updateFirmware(softwareId, fileName, version, url,
   * sizeBytes)` (needs a partner-signed app). Absent where the shell can't
   * trigger an OEM update - those devices don't report `otaFirmware`. The image
   * is OEM-supplied; we only point the TV at it. Must never throw.
   */
  applyFirmwareUpdate?(
    url: string,
    version: string,
    fileName: string,
    sizeBytes: number
  ): void;
  /**
   * How the shell flashes firmware - `"jedi"` (Philips MediaSuite: the TV's own
   * updater is reachable) or `"none"` (no trigger; excluded from firmware
   * rollouts). Needed because the SAME Android APK runs on plain STBs, where
   * `applyFirmwareUpdate` exists but has nothing to call. Absent on older shells
   * that only expose the trigger. Must never throw.
   */
  firmwareUpdateMode?(): string | null | undefined;
  /**
   * Optional display power control (Phase 5 - scheduled on/off). `on:false`
   * puts the panel into standby (Philips JEDI / Tizen / webOS); the app keeps
   * running. Absent where the shell can't control power - those devices don't
   * report `powerControl`. Returns the TV's verdict (true = the state change
   * was accepted); an older shell that returns nothing is read as a failure,
   * since an unconfirmed switch must never be counted as an energy saving.
   * Must never throw.
   */
  setPower?(on: boolean): boolean | void;
  /**
   * Whether the shell can REALLY switch the panel - `"jedi"` (Philips
   * MediaSuite, TV services up) or `"none"`. Needed because the SAME Android
   * APK runs on plain STBs, where `setPower` exists but has nothing to call.
   * Absent on older shells (which don't expose `setPower` either). Must never
   * throw.
   */
  powerControlMode?(): string | null | undefined;
  /**
   * How this box is managed - `"device-owner"` (enrolled at staging: the shell
   * self-grants camera/mic, installs APKs silently and can reboot the device),
   * `"profile-owner"` (a work-profile enrolment; possible on a tablet, useless
   * for a TV, reported rather than folded into "none" so a mis-provisioned box
   * is visible) or `"none"` (hand-held: whatever adb granted it once). Absent on
   * shells that predate enrolment - read as `"none"`, never as "probably fine".
   * Must never throw.
   */
  managementMode?(): string | null | undefined;
  /**
   * Optional real device reboot (device-owner only). Returns the OS verdict:
   * `false` means we are not privileged and the caller must fall back to
   * reloading the page. Absent on shells that predate enrolment - those devices
   * don't report `rebootDevice`. Must never throw.
   */
  rebootDevice?(): boolean | null | undefined;
  /**
   * Optional NTP (time) server configuration (OTA add-on - `set_ntp`). Points the
   * device's clock source at `server` (Android: `Settings.Global.NTP_SERVER` /
   * systemProperties, needs a privileged shell). Absent where the shell can't set
   * it - those devices don't report `setNtp`. Must never throw.
   */
  setNtpServer?(server: string): void;
  /**
   * Optional kiosk-return-guard suspend, for `FoyerBridge
   * .unlockKioskForProSettings()`. Absent on any shell with no kiosk-reclaim
   * behaviour to suspend (Tizen, a plain STB, web). Must never throw.
   */
  unlockKioskForProSettings?(): void;
  /**
   * Optional native DASH+DRM fallback (PLAYER-6), for `FoyerBridge
   * .startNativePlayback()`. Absent on any shell without it (older APKs,
   * Tizen, web) - the bridge method returns false there. Must never throw.
   */
  startNativePlayback?(url: string, licenceUrl: string): boolean;
  /** Optional stop for the same fallback. Absent wherever `startNativePlayback`
   *  is. Must never throw. */
  stopNativePlayback?(): boolean;
  /**
   * Optional firmware guest-data clear at checkout. Returns JSON
   * `{"cleared","method","detail"}` (Philips MediaSuite → JEDI
   * `IPMS.checkOutSuccessful()`, which is what triggers the firmware's own
   * clear of native app logins). Absent where the shell can't trigger one.
   * Must never throw.
   */
  clearGuestCredentials?(): string | null | undefined;
  /**
   * Whether the shell can REALLY run that clear - `"jedi-pms"` or `"none"`.
   * Needed because the SAME Android APK runs on plain STBs, where the method
   * exists but has nothing to call. Must never throw.
   */
  guestDataMode?(): string | null | undefined;
  /**
   * Optional firmware check-IN signal. Returns JSON `{"ok","method","detail"}`
   * (Philips MediaSuite → JEDI `IPMS.checkInSuccessful()`). Absent where the
   * shell can't send one. Must never throw.
   */
  signalGuestCheckIn?(): string | null | undefined;
  /** Whether the shell can REALLY send it - `"jedi-pms"` or `"none"`. */
  guestCheckInMode?(): string | null | undefined;
  /**
   * Optional video-call readiness probe. Answers whether THIS box could carry a
   * call right now, not whether the shell was built with calling in it:
   *   - `"camera2"`      - a capture device is enumerated and CAMERA /
   *                        RECORD_AUDIO are granted: a call can start,
   *   - `"no-camera"`    - the shell is call-capable but nothing is plugged in
   *                        (or the webcam died / was unplugged by a resident),
   *   - `"no-permission"`- a camera exists but the OS hasn't granted capture
   *                        (an un-provisioned box; the grant is requested at
   *                        call start, so it can't be promised in advance),
   *   - `"none"`         - no camera stack at all.
   * The distinction is the point: it's what lets support answer "is the camera
   * even enumerated?" from a desk instead of a site visit. Absent on shells that
   * predate video calling. Must never throw.
   */
  videoCallMode?(): string | null | undefined;
  /**
   * Arm the native capture gate for a call (VC-13). Until this is called every
   * `getUserMedia` in the page is denied natively, for any origin but ours, and
   * the audio stack is put in MODE_IN_COMMUNICATION. Returns whether the OS
   * media permissions are already held - `false` means a runtime request was
   * just raised and the page should wait for the `foyer:media-permissions`
   * event before retrying capture rather than reporting a hardware failure.
   * Absent on shells that predate video calling.
   */
  startCallSession?(): boolean | null | undefined;
  /**
   * Disarm the capture gate and restore the audio mode (VC-13). Called on every
   * exit from a call - ended, declined, timed out, error, unmount - and safe to
   * call twice. The shell also calls it from onPause/onDestroy, so a web layer
   * that forgets cannot leave the camera armed. Absent on older shells.
   */
  endCallSession?(): void;
  /**
   * Whether the native capture gate is currently open (VC-13). A pure read, used
   * to reconcile after a lifecycle event the shell may have handled under us.
   * Absent on older shells.
   */
  isCallSessionActive?(): boolean | null | undefined;
  /**
   * The device's FCM registration token (VC-28), or null/empty when Firebase
   * isn't configured on this build. Read on each heartbeat and reported to the
   * server so an inbound call can wake the box from Doze. Absent on shells that
   * predate the FCM ring path. Must never throw.
   */
  getFcmToken?(): string | null | undefined;
}

/**
 * Samsung Tizen B2B (hospitality) input-source control. Tizen has no native
 * shell - the Foyer app talks to the OS globals directly. The exact method
 * shape varies by firmware/SDK; everything here is feature-detected and
 * guarded, so a TV that doesn't expose it simply reports `switchInput: false`.
 */
interface TizenB2BControl {
  getInputSourceList?(
    onSuccess: (
      list: Array<{
        source?: string | number;
        type?: string;
        label?: string;
        connect?: boolean;
      }>
    ) => void,
    onError?: (e: unknown) => void
  ): void;
  setInputSource?(
    source: string | number,
    onSuccess?: () => void,
    onError?: (e: unknown) => void
  ): void;
  /**
   * Screen-mirroring (Miracast / "Smart View") control on Samsung hospitality
   * firmware. Like the input API above, the exact shape varies by firmware and
   * everything here is feature-detected and guarded - a TV that doesn't expose
   * it simply reports `supported: false` in {@link CastingInfo}.
   */
  getScreenMirroring?(
    onSuccess: (state: { enable?: boolean } | boolean) => void,
    onError?: (e: unknown) => void
  ): void;
  setScreenMirroring?(
    enable: boolean,
    onSuccess?: () => void,
    onError?: (e: unknown) => void
  ): void;
}

/**
 * Apple iOS / tvOS native shell seam. A WKWebView shell injects a message
 * handler named `foyerNative` (`window.webkit.messageHandlers.foyerNative`);
 * calling `postMessage(scheme)` hands the native side a custom URL scheme to
 * open the app. Absent in plain Mobile Safari, so its presence - not the UA -
 * is what marks the "ios" platform (mirroring Android's `FoyerNative`).
 */
interface WebKitMessageHandler {
  postMessage(message: unknown): void;
}

declare global {
  interface Window {
    tizen?: { application?: TizenApplicationManager };
    webOS?: { service?: WebOSService };
    FoyerNative?: FoyerNativeBridge;
    /** Samsung Tizen hospitality (B2B) API namespace. */
    b2bapis?: { b2bcontrol?: TizenB2BControl };
    /**
     * LG Pro:Centric hospitality API (HCAP), injected by webOS hospitality
     * firmware. Untyped beyond the module split because the surface is
     * partner-SDK documented and varies by firmware - everything we touch is
     * feature-detected by name at call time.
     */
    hcap?: { property?: Record<string, unknown> } & Record<string, unknown>;
    /** Apple WKWebView message-handler bridge (iOS / tvOS native shell). */
    webkit?: {
      messageHandlers?: { foyerNative?: WebKitMessageHandler };
    };
  }
}

/** Where are we running? Prefers concrete capabilities over UA sniffing. */
export function detectPlatform(): TvPlatform {
  if (typeof window === "undefined") return "web";
  if (typeof window.FoyerNative?.launchApp === "function") return "android";
  if (typeof window.tizen?.application?.launch === "function") return "tizen";
  if (typeof window.webOS?.service?.request === "function") return "webos";
  // Apple iOS / tvOS: only when the native WKWebView shell injected its message
  // handler - plain Mobile Safari has no such handler, so it stays "web".
  if (
    typeof window.webkit?.messageHandlers?.foyerNative?.postMessage ===
    "function"
  ) {
    return "ios";
  }
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent || "";
  if (/Tizen/i.test(ua)) return "tizen";
  if (/Web0S|webOS/i.test(ua)) return "webos";
  if (/Android/i.test(ua)) return "android";
  return "web";
}

/**
 * The launch identifier for `app` on `platform`, if it carries one. Re-exported
 * from the shared resolver ({@link resolveLaunchTarget}) so the platform → field
 * mapping lives in exactly one place, shared with the server and the CMS.
 */
export function launchIdFor(
  app: LaunchableApp,
  platform: TvPlatform
): string | undefined {
  return sharedLaunchIdFor(app, platform);
}

function dispatchLaunch(app: LaunchableApp, platform: TvPlatform): boolean {
  const target = resolveLaunchTarget(app, platform);
  if (target.kind !== "native") {
    // No native id for this unit. If the resolver found a web address, open
    // that instead - in a new context, so the Foyer screen itself never
    // navigates away (a kiosk that leaves the app has no way back).
    if (target.kind === "web" && target.id) {
      try {
        window.open(target.id, "_blank", "noopener");
        return true;
      } catch {
        /* fall through to the warning below */
      }
    }
    if (platform !== "web") {
      console.warn(
        `[FoyerBridge] no ${platform} id for "${app.name ?? "app"}"`
      );
    }
    return false;
  }
  const id = target.id!;
  // We are about to hand the screen away on purpose, so the backgrounding that
  // follows must not be logged as the panel being taken from us. "A departure
  // we caused is never reversed" - see lib/foregroundWatch.ts.
  expectForegroundLoss();
  try {
    switch (platform) {
      // Android: the shell's FoyerNative bridge. On MediaSuite the Kotlin side
      // routes this through JEDI IApplicationControl; on the STB, an intent.
      case "android":
        window.FoyerNative!.launchApp(id);
        return true;
      case "tizen": {
        // Refuse an application this panel does not have, when we know. The
        // alternative is what the bench found: a tile that does nothing at all.
        const installed = installedAppIds();
        if (installed && !installed.has(id)) {
          console.warn(
            `[launch] ${id} is not installed on this panel - refusing rather than pressing a dead tile`
          );
          return false;
        }
        // Tizen's launch is ASYNCHRONOUS and reports through callbacks we were
        // not passing, so every failure - missing app, denied privilege - was
        // swallowed while this returned true. We still return true (the launch
        // was dispatched and the panel may be mid-switch), but a failure now
        // says so in the log capture instead of vanishing.
        window.tizen!.application!.launch(
          id,
          () => {},
          (e: unknown) =>
            console.warn(
              `[launch] tizen refused ${id}: ${e instanceof Error ? e.message : String(e)}`
            )
        );
        return true;
      }
      case "webos":
        window.webOS!.service!.request("luna://com.webos.applicationManager", {
          method: "launch",
          parameters: { id, params: {} },
        });
        return true;
      // Apple iOS / tvOS: hand the WKWebView shell the custom URL scheme; the
      // native side resolves it to the installed app via UIApplication.open.
      case "ios":
        window.webkit!.messageHandlers!.foyerNative!.postMessage(id);
        return true;
      default:
        return false;
    }
  } catch (err) {
    console.warn(`[FoyerBridge] failed to launch "${app.name ?? id}"`, err);
    return false;
  }
}

/** Trim a value to a non-empty string, or undefined. */
function cleanField(v: unknown): string | undefined {
  if (typeof v !== "string") return undefined;
  const t = v.trim();
  return t.length ? t : undefined;
}

/**
 * Pull device identity from the native shell, normalizing the object-or-JSON
 * contract and dropping empty/garbage fields. Returns null when nothing usable
 * is available - including a parse failure, which must never crash the screen.
 */
/**
 * Device identity on Samsung Tizen, read from `webapis.productinfo`.
 *
 * MEASURED ON A PANEL 2026-08-10. Two things this closes:
 *
 *  - The CMS listed a real Samsung panel as "Unknown TV", because
 *    `guessModel()` falls through to `navigator.platform` and a Tizen UA
 *    carries no model token. `getRealModel()` answers `QB24C`.
 *  - `serial` was undefined, so the redeem call sent none - and serial is what
 *    Foyer's re-attach uses to recognise a screen that lost its storage.
 *    **On Tizen the DUID IS the serial** (TIZEN-7): `getDuid()` is stable
 *    across reinstalls and storage loss, which is exactly the property
 *    re-attach needs.
 *
 * Requires the privileged context, so it only answers when the app is running
 * inside the WGT (see tizen/index.html - transcluded, never navigated to) AND
 * the widget declares `http://developer.samsung.com/privilege/productinfo`.
 * Without either, every call throws "Permission denied" and we return null and
 * fall back to `guessModel()` - which is the honest answer, not a broken one.
 */
function readTizenDeviceInfo(): DeviceInfo | null {
  const pi = (
    window as unknown as {
      webapis?: { productinfo?: Record<string, () => unknown> };
    }
  ).webapis?.productinfo;
  if (!pi) return null;

  const call = (fn: string): string | undefined => {
    try {
      const f = pi[fn];
      return typeof f === "function" ? cleanField(String(f())) : undefined;
    } catch {
      // "Permission denied" when the privilege is not declared. Not a fault -
      // just an absence, and the caller has a fallback.
      return undefined;
    }
  };

  const info: DeviceInfo = {};
  const model = call("getRealModel") ?? call("getModel");
  const firmware = call("getFirmware");
  const serial = call("getDuid");
  if (model) info.model = model;
  if (firmware) info.firmware = firmware;
  if (serial) info.serial = serial;
  return info.model || info.firmware || info.serial ? info : null;
}

function readDeviceInfo(): DeviceInfo | null {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  // No Android shell here? It may still be a Samsung panel with a real
  // privileged context - ask Tizen before giving up and guessing from the UA.
  if (typeof native?.getDeviceInfo !== "function") return readTizenDeviceInfo();
  let raw: DeviceInfo | string | null | undefined;
  try {
    raw = native.getDeviceInfo();
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const src = parsed as Partial<DeviceInfo>;
  const info: DeviceInfo = {};
  const model = cleanField(src.model);
  const firmware = cleanField(src.firmware);
  const serial = cleanField(src.serial);
  if (model) info.model = model;
  if (firmware) info.firmware = firmware;
  if (serial) info.serial = serial;
  return info.model || info.firmware || info.serial ? info : null;
}

/**
 * The version of the NATIVE CONTAINER, as distinct from the web bundle.
 *
 * On Android that is the installed APK. On Samsung it is the WGT, and until now
 * it was simply absent: `appVersion()` only knew how to ask `FoyerNative`, so a
 * Tizen panel reported null and the fleet fell back to the bundle version. The
 * consequence is the one TIZEN-13 exists to prevent - **a panel running a stale
 * widget is indistinguishable from one running the current one**, because the
 * bundle is fetched fresh on every boot and always looks new. The widget is the
 * part that only changes when `sssp_config.xml` says so, and it is the part
 * that carries the bootloader, the privileges and the key claims.
 *
 * `getCurrentApplication().appInfo.version` is the widget's `config.xml`
 * version - the same number the build script keeps in lockstep with
 * `sssp_config.xml`'s `<ver>`, so "which widget is this panel on?" becomes
 * answerable from a desk.
 */
function readAppVersion(): string | null {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.appVersion === "function") {
    try {
      return cleanField(native.appVersion()) ?? null;
    } catch {
      return null;
    }
  }
  try {
    const app = (
      window as unknown as {
        tizen?: {
          application?: {
            getCurrentApplication?: () => {
              appInfo?: { version?: string };
            };
          };
        };
      }
    ).tizen?.application;
    if (typeof app?.getCurrentApplication !== "function") return null;
    return cleanField(app.getCurrentApplication()?.appInfo?.version) ?? null;
  } catch {
    // Not privileged, or a firmware that answers differently. Null is honest -
    // the caller falls back to the bundle version.
    return null;
  }
}

/** Read the streaming-app version, normalized to a non-empty string or null. */
function readStreamingAppVersion(packageSlug?: string): string | null {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.streamingAppVersion !== "function") return null;
  try {
    return cleanField(native.streamingAppVersion(packageSlug ?? "")) ?? null;
  } catch {
    return null;
  }
}

/** Trim a value to a finite number, or undefined. */
function cleanNumber(v: unknown): number | undefined {
  const n = typeof v === "string" ? Number(v) : v;
  return typeof n === "number" && Number.isFinite(n) ? n : undefined;
}

/**
 * Pull live network state from the native shell, normalizing the object-or-JSON
 * contract and dropping empty/garbage fields. Returns null when nothing usable
 * is available - including a parse failure, which must never crash the screen.
 */
/**
 * Live network state on Samsung Tizen, from `webapis.network`.
 *
 * Reported missing from the CMS device page 2026-08-10 (no signal, no wired,
 * no ethernet IP) - correctly, because there was no Tizen branch here at all.
 *
 * Tizen splits what Android hands over in one object:
 *   getActiveConnectionType() -> which interface is actually carrying traffic
 *   getIp()                   -> the IPv4 of THAT interface, not of both
 *   getWiFiSignalStrengthLevel() -> a 0..4 BAR LEVEL, not dBm
 *
 * The bar level is the interesting one. `NetworkInfo.wifiSignal` is documented
 * as dBm and the fleet's health rules read it that way, so handing them a `3`
 * would render as an absurdly strong -3 dBm and quietly poison every Wi-Fi
 * comparison across the estate. It is mapped onto representative dBm midpoints
 * instead, and a level we do not recognise is omitted rather than guessed -
 * an absent reading is honest, a wrong one is not.
 */
const TIZEN_WIFI_BARS_TO_DBM: Record<number, number> = {
  0: -95,
  1: -85,
  2: -75,
  3: -65,
  4: -50,
};

/**
 * Is the active interface wired?
 *
 * The first version of this hardcoded `1 === wired`, which was simply WRONG:
 * Samsung's `NetworkActiveConnectionType` is DISCONNECTED 0, WIFI 1, CELLULAR
 * 2, ETHERNET 3. A wired panel therefore reported its address as `wifiIp`, the
 * CMS showed no ethernet IP and no wired badge, and we then asked a cabled TV
 * for a Wi-Fi bar level - which is exactly the "no signal, no wired, no
 * ethernet IP" Sigge saw on 2026-08-10.
 *
 * The lesson is not "use 3": it is that a magic number copied from
 * documentation is a guess with a citation. The firmware ships the enum, so we
 * ask IT what ETHERNET means and only fall back to the documented value when
 * the constant is missing. A firmware that renumbers the enum then stays
 * correct here, and the string forms some builds return still match.
 */
function isWiredConnection(
  net: Record<string, unknown>,
  raw: unknown
): boolean {
  if (typeof raw === "string" && /ethernet|wired/i.test(raw)) return true;
  const enums = net["NetworkActiveConnectionType"] as
    | Record<string, unknown>
    | undefined;
  const ethernet = cleanNumber(enums?.["ETHERNET"]) ?? 3;
  return cleanNumber(raw) === ethernet;
}

function readTizenNetworkInfo(): NetworkInfo | null {
  const net = (
    window as unknown as {
      webapis?: { network?: Record<string, unknown> };
    }
  ).webapis?.network;
  if (!net) return null;

  const call = (fn: string): unknown => {
    try {
      const f = net[fn];
      return typeof f === "function"
        ? (f as () => unknown).call(net)
        : undefined;
    } catch {
      return undefined;
    }
  };

  const info: NetworkInfo = {};
  const ip = cleanField(String(call("getIp") ?? ""));
  const wired = isWiredConnection(net, call("getActiveConnectionType"));
  if (ip) {
    if (wired) info.ethIp = ip;
    else info.wifiIp = ip;
  }

  if (!wired) {
    const bars = cleanNumber(call("getWiFiSignalStrengthLevel"));
    if (bars !== undefined && bars in TIZEN_WIFI_BARS_TO_DBM) {
      info.wifiSignal = TIZEN_WIFI_BARS_TO_DBM[bars];
    }
  }

  return info.wifiIp || info.ethIp || info.wifiSignal !== undefined
    ? info
    : null;
}

function readNetworkInfo(): NetworkInfo | null {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  // No Android shell? A Samsung panel still knows its own network - ask Tizen
  // before reporting nothing.
  if (typeof native?.getNetworkInfo !== "function")
    return readTizenNetworkInfo();
  let raw: NetworkInfo | string | null | undefined;
  try {
    raw = native.getNetworkInfo();
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const src = parsed as Partial<NetworkInfo>;
  const info: NetworkInfo = {};
  const wifiIp = cleanField(src.wifiIp);
  const ethIp = cleanField(src.ethIp);
  const wifiSignal = cleanNumber(src.wifiSignal);
  if (wifiIp) info.wifiIp = wifiIp;
  if (ethIp) info.ethIp = ethIp;
  if (wifiSignal !== undefined) info.wifiSignal = wifiSignal;
  return info.wifiIp || info.ethIp || info.wifiSignal !== undefined
    ? info
    : null;
}

// ── External inputs (HDMI / AV) ─────────────────────────────────────────────

/** Normalize a raw input entry into an InputSource, or null if unusable. */
function toInputSource(
  s: Partial<InputSource> | undefined
): InputSource | null {
  const id = cleanField(s?.id);
  if (!id) return null;
  return {
    id,
    label: cleanField(s?.label) ?? id,
    connected: typeof s?.connected === "boolean" ? s.connected : undefined,
  };
}

/** Inputs from the Android/MediaSuite shell (object or JSON), or []. */
function readNativeInputs(): InputSource[] {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.listInputs !== "function") return [];
  let raw: InputSource[] | string | null | undefined;
  try {
    raw = native.listInputs();
  } catch {
    return [];
  }
  if (!raw) return [];
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((s) => toInputSource(s as Partial<InputSource>))
    .filter((s): s is InputSource => s !== null);
}

// ── Guest casting (SoftAP hotspot + Chromecast) ─────────────────────────────

/**
 * Read guest-casting state from the Android/MediaSuite shell (object or JSON),
 * or null when the shell doesn't expose it. A parse failure or a throw returns
 * null - it must never crash the screen.
 */
function readGuestCastingStatus(): GuestCastingStatus | null {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.guestCastingStatus !== "function") return null;
  let raw: GuestCastingStatus | string | null | undefined;
  try {
    raw = native.guestCastingStatus();
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const src = parsed as Partial<GuestCastingStatus>;
  if (typeof src.supported !== "boolean") return null;
  return {
    supported: src.supported,
    hotspotEnabled: src.hotspotEnabled === true,
    castEnabled: src.castEnabled === true,
    ssid: cleanField(src.ssid),
    password: cleanField(src.password),
  };
}

/**
 * Normalize the Android/JEDI {@link GuestCastingStatus} into the cross-platform
 * {@link CastingInfo}. Philips MediaSuite runs a Google Cast receiver that makes
 * its OWN Wi-Fi Direct group and shows its own on-screen code (see
 * SharingProvider.kt) - so the transport is "wifi-direct" and the receiver must
 * be launched when the guest opens Screen Share. When the firmware reports a
 * live SoftAP instead, we surface that as "hotspot" with its credentials.
 */
/**
 * Read the natively-resolved screen-share target (object or JSON), or null when
 * the shell predates the resolver / the read threw. Never throws.
 */
function readScreenShareTarget(): ScreenShareTarget | null {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.screenShareTarget !== "function") return null;
  let raw: ScreenShareTarget | string | null | undefined;
  try {
    raw = native.screenShareTarget();
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const src = parsed as Partial<ScreenShareTarget>;
  if (typeof src.available !== "boolean") return null;
  return {
    available: src.available,
    launchable: src.launchable === true,
    kind: cleanField(src.kind),
    id: cleanField(src.id),
    label: cleanField(src.label),
    protocol: cleanField(src.protocol),
    deviceName: cleanField(src.deviceName),
    candidates: Array.isArray(src.candidates)
      ? src.candidates.filter((c): c is string => typeof c === "string")
      : undefined,
  };
}

/**
 * Normalize a natively-resolved {@link ScreenShareTarget} into the shared
 * {@link CastingInfo}. `launchable` is the field that decides the whole guest
 * flow: true means the Screen Share tile hands the screen to the device's own
 * receiver, false means Chromecast built-in is listening with nothing to open
 * and the guest starts the cast from their phone.
 */
function castingFromTarget(t: ScreenShareTarget): CastingInfo {
  const protocol: CastProtocol =
    t.protocol === "miracast" ? "miracast" : "chromecast";
  return {
    supported: true,
    // The receiver is there either way; on a launchable target it comes up when
    // the guest opens it, on Chromecast built-in it is permanently listening.
    enabled: true,
    protocols: [protocol],
    // A mirroring app makes its own peer-to-peer group; Chromecast built-in is
    // discovered over the house network the guest is already on.
    transport: t.launchable ? "wifi-direct" : "lan",
    launchable: t.launchable,
    deviceName: t.deviceName,
  };
}

function castingFromAndroid(s: GuestCastingStatus): CastingInfo {
  const hotspot = s.hotspotEnabled && !!s.ssid;
  return {
    supported: true,
    enabled: s.castEnabled || s.hotspotEnabled,
    protocols: ["chromecast"],
    transport: hotspot ? "hotspot" : "wifi-direct",
    launchable: true,
    ssid: hotspot ? s.ssid : undefined,
    password: hotspot ? s.password : undefined,
  };
}

// Tizen answers asynchronously, so cache the last result; the sync listInputs()
// returns the cache while a refresh runs in the background.
let tizenInputs: InputSource[] = [];

// Same async-cache shape for Tizen screen mirroring: `null` until the first
// answer lands, then the last-known enabled flag.
let tizenMirroring: boolean | null = null;
// …and for the webOS Miracast service (luna requests are callback-based too).
let webosMirroring: boolean | null = null;

function tizenControl(): TizenB2BControl | undefined {
  return typeof window === "undefined" ? undefined : window.b2bapis?.b2bcontrol;
}

/**
 * `webapis.systemcontrol` - Samsung's privileged device-operations namespace
 * (TIZEN-13). Present only inside the signed widget with
 * `developer.samsung.com/privilege/systemcontrol` declared, which config.xml
 * does. Confirmed present on a QB24C, firmware T-INFOLINK2023-1019.
 */
function tizenSystemControl(): Record<string, unknown> | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return (
      window as unknown as {
        webapis?: { systemcontrol?: Record<string, unknown> };
      }
    ).webapis?.systemcontrol;
  } catch {
    return undefined;
  }
}

/**
 * The first of `names` that this firmware actually exposes as a function.
 *
 * Samsung renamed these calls across SSSP generations and the name is not
 * something we can settle from documentation - the panel is the authority. So
 * rather than hardcode one guess and ship a capability that silently no-ops,
 * every device operation below asks the namespace what it has, and reports
 * itself unavailable when the answer is nothing. That is the same rule
 * `canControlPower` and `canRebootDevice` already apply to the Android shell:
 * a method we cannot name is a method we must not claim.
 *
 * The `?probe=1` page prints `webapis.systemcontrol.*` in full, so the next
 * photograph from the bench either confirms one of these names or gives us the
 * real one to add.
 */
function tizenSysMethod(
  names: string[]
): ((...a: unknown[]) => unknown) | null {
  const sc = tizenSystemControl();
  if (!sc) return null;
  for (const n of names) {
    try {
      const f = sc[n];
      if (typeof f === "function") return f.bind(sc) as () => unknown;
    } catch {
      // A getter that throws is not a method we have.
    }
  }
  return null;
}

/**
 * Candidate names for "restart this television", newest SSSP first.
 * CONFIRMED on a QB24C / T-INFOLINK2023-1019: this firmware has `rebootDevice`
 * and no `reboot`, so the probe was right to ask the panel rather than trust a
 * datasheet - the first candidate does not exist here.
 */
const TIZEN_REBOOT = ["reboot", "rebootDevice", "setSystemReboot"];

/** Candidate names for a framebuffer grab. CONFIRMED: `captureScreen`. */
const TIZEN_CAPTURE = ["captureScreen", "screenCapture", "getScreenCapture"];

/**
 * OEM firmware update on Samsung (TEP firmware OTA).
 *
 * MEASURED 2026-10-08 on an HU8000F (Tizen 9, T-RSLFDEUC-0090-1166.3) inside
 * the Nenda-signed widget: `webapis.systemcontrol.updateFirmware` is present
 * and `setUpdateFirmwareListener` passes the privilege check (the probe's
 * `perm systemcontrol.setUpdateFirmwareListener=ok`). Samsung's documented
 * signature is `updateFirmware(SoftwareID, SWUFileName, SWVersion, SWURL,
 * SWTotalBytes)` with SoftwareID "0" and a `.bem` image (the BEM package, NOT
 * the USB zip). The TV fetches the image itself and reboots to flash.
 */
const TIZEN_UPDATE_FIRMWARE = ["updateFirmware"];

/** Verdict of a clone restore, as acked to the fleet console. */
export interface CloneRestoreResult {
  applied: boolean;
  /** Local path handed to restoreCloneData, when we got that far. */
  path?: string;
  bytes?: number;
  error?: string;
  /** Every path tried, with the TV's verdict on each. */
  attempts?: CloneAttempt[];
}

interface CloneWriteBridge {
  /** The WGT's wgt-private writer (tizen/index.html); returns a file:// URI. */
  foyerCacheWrite?: (filename: string, base64: string) => Promise<string>;
}

function tizenClone(): Record<string, unknown> | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return (
      window as unknown as { webapis?: { clone?: Record<string, unknown> } }
    ).webapis?.clone;
  } catch {
    return undefined;
  }
}

function canRestoreClone(): boolean {
  const c = tizenClone();
  return (
    typeof c?.restoreCloneData === "function" &&
    typeof (window as unknown as CloneWriteBridge).foyerCacheWrite ===
      "function"
  );
}

function downloadArrayBuffer(url: string): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url);
    xhr.responseType = "arraybuffer";
    xhr.timeout = 120_000;
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve(xhr.response as ArrayBuffer)
        : reject(new Error(`HTTP ${xhr.status}`));
    xhr.onerror = () => reject(new Error("network error"));
    xhr.ontimeout = () => reject(new Error("timeout"));
    xhr.send();
  });
}

function errText(e: unknown): string {
  return e instanceof Error ? `${e.name}: ${e.message}` : String(e);
}

/**
 * TEP cloning on Samsung. MEASURED 2026-10-08 (HU8000F, Tizen 9): the wrapper
 * is `restoreCloneData(cloneFilePath)` -> native RestoreCloneData{stringPath},
 * and `setCloneUpdateStatusListener` passes the privilege check.
 *
 * MEASURED 2026-10-09: a lone `foyer-clone.sclone` in the widget's private
 * data dir is refused 23 ms after the call with
 * `{"Code":3,"Message":"Clone Internal Error","Result":"FAIL"}` - the verdict
 * arrives on the status listener, the call itself never throws. Whether
 * Samsung wants its own USB layout (`<family>/sClone/CloneEncrypted.sclone`),
 * the folder rather than the file, or a location its clone daemon may read is
 * not documented, so the restore walks a short list of candidates - the USB
 * stick in the TV first, as the ground truth - and stops at the first one the
 * TV does not refuse. Every attempt and verdict is logged and acked.
 */
const SAMSUNG_CLONE_NAME = "CloneEncrypted.sclone";
/** How long to wait for the listener's verdict on one attempt. */
const CLONE_VERDICT_WAIT_MS = 20_000;

export interface CloneAttempt {
  /** Where the candidate came from: usb-file, downloads-folder, ... */
  via: string;
  path?: string;
  /** The listener's verdict, verbatim (parsed when it was JSON). */
  status?: unknown;
  error?: string;
}

type TizenFs = {
  listStorages?: (
    ok: (s: Array<{ label: string; type: string; state: string }>) => void,
    err?: (e: unknown) => void
  ) => void;
  resolve?: (
    loc: string,
    ok: (dir: TizenFile) => void,
    err?: (e: unknown) => void,
    mode?: string
  ) => void;
  createDirectory?: (path: string, makeParents?: boolean) => void;
  openFile?: (
    path: string,
    mode: string
  ) => { writeData: (d: Uint8Array) => void; close: () => void };
  toURI?: (path: string) => string;
};
type TizenFile = {
  name: string;
  isDirectory: boolean;
  toURI: () => string;
  resolve: (rel: string) => TizenFile;
  listFiles: (ok: (f: TizenFile[]) => void, err?: (e: unknown) => void) => void;
};

function tizenFs(): TizenFs | undefined {
  try {
    return (window as unknown as { tizen?: { filesystem?: TizenFs } }).tizen
      ?.filesystem;
  } catch {
    return undefined;
  }
}

function uriToPath(uri: string): string {
  return decodeURI(String(uri).replace(/^file:\/\//, ""));
}

/** One listener for the session; each attempt borrows it while it waits. */
let cloneVerdictSink: ((status: unknown) => void) | null = null;
const cloneListened = new WeakSet<object>();

function ensureCloneListener(clone: Record<string, unknown>): void {
  if (cloneListened.has(clone)) return;
  const listen = clone.setCloneUpdateStatusListener;
  if (typeof listen !== "function") return;
  try {
    listen.call(clone, (status: unknown) => {
      try {
        console.info(`[clone] tizen restore status ${JSON.stringify(status)}`);
      } catch {
        console.info("[clone] tizen restore status (unserialisable)");
      }
      cloneVerdictSink?.(status);
    });
    cloneListened.add(clone);
  } catch (e) {
    // "Listener Already Set" from an earlier page life: the old callback
    // still logs, we just cannot wait on it.
    console.info(`[clone] status listener: ${errText(e)}`);
  }
}

function parseCloneStatus(status: unknown): unknown {
  if (typeof status !== "string") return status;
  try {
    return JSON.parse(status);
  } catch {
    return status;
  }
}

function isCloneFail(status: unknown): boolean {
  const s = status as { Result?: unknown } | null;
  return (
    typeof s === "object" &&
    s !== null &&
    String(s.Result).toUpperCase() === "FAIL"
  );
}

/** Call restoreCloneData(path) and wait for the listener's verdict. */
function restoreAndAwait(
  clone: Record<string, unknown>,
  path: string,
  via: string
): Promise<CloneAttempt> {
  return new Promise((resolve) => {
    const done = (a: CloneAttempt) => {
      clearTimeout(timer);
      cloneVerdictSink = null;
      resolve(a);
    };
    const timer = setTimeout(
      () => done({ via, path }),
      cloneListened.has(clone) ? CLONE_VERDICT_WAIT_MS : 0
    );
    cloneVerdictSink = (status) =>
      done({ via, path, status: parseCloneStatus(status) });
    try {
      console.info(`[clone] tizen restoreCloneData (${via}) ${path}`);
      (clone.restoreCloneData as (p: string) => unknown).call(clone, path);
    } catch (e) {
      console.info(`[clone] tizen restoreCloneData refused: ${errText(e)}`);
      done({ via, path, error: errText(e) });
    }
  });
}

/** `<family>/sClone/CloneEncrypted.sclone` on a USB stick in the TV. */
function findUsbClone(folder: string): Promise<string | null> {
  const fs = tizenFs();
  if (!fs?.listStorages || !fs.resolve) return Promise.resolve(null);
  const resolveDir = fs.resolve.bind(fs);
  return new Promise((resolve) => {
    const giveUp = setTimeout(() => resolve(null), 5_000);
    const finish = (p: string | null) => {
      clearTimeout(giveUp);
      resolve(p);
    };
    try {
      fs.listStorages!(
        (storages) => {
          const usb = storages.filter(
            (s) => s.type === "EXTERNAL" && s.state === "MOUNTED"
          );
          console.info(
            `[clone] storages ${storages.map((s) => `${s.label}:${s.type}:${s.state}`).join(", ")}`
          );
          const next = (i: number) => {
            if (i >= usb.length) return finish(null);
            resolveDir(
              usb[i]!.label,
              (dir) => {
                dir.listFiles(
                  (files) => {
                    const pick =
                      files.find((f) => f.isDirectory && f.name === folder) ??
                      files.find((f) => f.isDirectory && /^T-/i.test(f.name));
                    if (!pick) return next(i + 1);
                    try {
                      finish(
                        uriToPath(
                          dir
                            .resolve(
                              `${pick.name}/sClone/${SAMSUNG_CLONE_NAME}`
                            )
                            .toURI()
                        )
                      );
                    } catch {
                      next(i + 1);
                    }
                  },
                  () => next(i + 1)
                );
              },
              () => next(i + 1),
              "r"
            );
          };
          next(0);
        },
        () => finish(null)
      );
    } catch {
      finish(null);
    }
  });
}

/** Write the clone in Samsung's USB layout under a virtual root. */
function writeSamsungLayout(
  root: string,
  folder: string,
  buf: ArrayBuffer
): { file: string; folder: string } {
  const fs = tizenFs();
  if (!fs?.createDirectory || !fs.openFile || !fs.toURI) {
    throw new Error("tizen.filesystem 5.0 API unavailable");
  }
  const base = `${root}/foyer-clone/${folder}`;
  try {
    fs.createDirectory(`${base}/sClone`, true);
  } catch (e) {
    // MEASURED 2026-10-09: Tizen throws `IOError: File exists` when the folder
    // is already there from an earlier restore. That is the state we want.
    if (!/exist/i.test(errText(e))) throw e;
  }
  const fh = fs.openFile(`${base}/sClone/${SAMSUNG_CLONE_NAME}`, "w");
  try {
    fh.writeData(new Uint8Array(buf));
  } finally {
    fh.close();
  }
  return {
    file: uriToPath(fs.toURI(`${base}/sClone/${SAMSUNG_CLONE_NAME}`)),
    folder: uriToPath(fs.toURI(base)),
  };
}

async function restoreTizenClone(
  url: string,
  folder: string
): Promise<CloneRestoreResult> {
  const clone = tizenClone();
  const write = (window as unknown as CloneWriteBridge).foyerCacheWrite;
  if (typeof clone?.restoreCloneData !== "function" || !write) {
    return { applied: false, error: "clone restore unavailable" };
  }
  const family = folder || "clone";
  let buf: ArrayBuffer;
  try {
    buf = await downloadArrayBuffer(url);
  } catch (e) {
    return { applied: false, error: `download: ${errText(e)}` };
  }
  console.info(`[clone] downloaded ${buf.byteLength}B from ${url}`);
  ensureCloneListener(clone);

  // Each candidate yields the paths to try, or throws why it could not.
  const candidates: Array<[string, () => Promise<string[]>]> = [
    [
      "usb",
      async () => {
        const p = await findUsbClone(family);
        if (!p) throw new Error("no clone folder on a USB stick in the TV");
        return [p, p.replace(/\/sClone\/[^/]+$/, "")];
      },
    ],
    [
      "downloads",
      async () => {
        const w = writeSamsungLayout("downloads", family, buf);
        return [w.file, w.folder];
      },
    ],
    [
      "wgt-private",
      async () => {
        const w = writeSamsungLayout("wgt-private", family, buf);
        return [w.file, w.folder];
      },
    ],
  ];

  const attempts: CloneAttempt[] = [];
  for (const [via, prepare] of candidates) {
    let paths: string[];
    try {
      paths = await prepare();
    } catch (e) {
      console.info(`[clone] ${via}: ${errText(e)}`);
      attempts.push({ via, error: errText(e) });
      continue;
    }
    for (const [i, path] of paths.entries()) {
      const a = await restoreAndAwait(
        clone,
        path,
        `${via}-${i === 0 ? "file" : "folder"}`
      );
      attempts.push(a);
      if (a.error || isCloneFail(a.status)) continue;
      // Not refused: either a verdict that is not FAIL, or no verdict yet
      // (the TV is busy restoring). Stop - never start a second restore.
      return {
        applied: a.status !== undefined,
        path,
        bytes: buf.byteLength,
        attempts,
        ...(a.status === undefined
          ? { error: "no verdict from the TV yet - watch the screen" }
          : {}),
      };
    }
  }
  return {
    applied: false,
    bytes: buf.byteLength,
    attempts,
    error: "the TV refused every candidate path",
  };
}

/** Software ID Samsung's own SystemControl sample passes for the main image. */
const TIZEN_FIRMWARE_SOFTWARE_ID = "0";

/**
 * Log the TV's own progress reports for a firmware update, so a later
 * `log_capture` shows what the panel did with the image (download progress,
 * verification failure, version rejected, ...). Best effort; never throws.
 */
function watchTizenFirmwareProgress(): void {
  const listen = tizenSysMethod(["setUpdateFirmwareListener"]);
  if (!listen) return;
  try {
    listen((...a: unknown[]) => {
      try {
        console.info(`[firmware] tizen update status ${JSON.stringify(a)}`);
      } catch {
        console.info("[firmware] tizen update status (unserialisable)");
      }
    });
  } catch (err) {
    console.info(
      `[firmware] tizen listener refused: ${err instanceof Error ? `${err.name}: ${err.message}` : String(err)}`
    );
  }
}

/**
 * Display power on Samsung, and why it is THIS pair of calls.
 *
 * TIZEN-13 deliberately shipped without power control, because the danger was
 * never "can this panel be switched off" - it was that the wrong call leaves a
 * television nobody can wake from the network, which is a van and an engineer
 * rather than a support ticket. The panel has now told us what it has, and
 * `setPanelMute` is exactly the Samsung analogue of the JEDI QUICK_CLOCK state
 * we use on Philips: the backlight goes off, the system stays up, the network
 * stays up, and Foyer keeps running.
 *
 * That last part is the safety argument. Unlike a system standby, a muted panel
 * cannot strand us, because the application that turned the backlight off is
 * still running and can turn it back on. There is no state here we cannot exit.
 *
 * And `getPanelMute` makes the ESG rule honest for the first time on this
 * platform: we do not have to trust a return value, we can READ BACK what the
 * hardware did. A switch we cannot confirm is not counted - see setPower.
 */
const TIZEN_PANEL_SET = ["setPanelMute"];
const TIZEN_PANEL_GET = ["getPanelMute"];

/**
 * Coerce a Samsung boolean-ish answer. These APIs are inconsistent about
 * returning `true`, `1`, or `"1"` for the same state across firmwares, and the
 * power read-back must not report "not confirmed" merely because a panel
 * answered with a number.
 */
function truthy(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  if (typeof v === "string") return v === "1" || v.toLowerCase() === "true";
  return false;
}

/**
 * Which applications are actually installed on this Samsung panel.
 *
 * `null` until the first answer lands, and `null` FOREVER off Tizen - the two
 * are the same thing to every caller: "unknown, do not filter on this". Only a
 * positive answer is ever acted on, because the failure mode of guessing here
 * is hiding a tile that would have worked.
 *
 * WHY THIS EXISTS. A catalogue entry with a `tizenId` set in the CMS resolves
 * to `kind: "native"` and renders a tile - whether or not that application is
 * on this television. The first real panel made the gap concrete: a QB24C
 * signage display carries 213 applications and not one of them is a consumer
 * app, so a Netflix tile configured in the CMS is a button that cannot lead
 * anywhere. `tizen.application.launch` on a missing id fails asynchronously,
 * through a callback we were not passing, so the tile did not even fail
 * visibly - it simply did nothing.
 *
 * The catalogue says what an operator INTENDED; this says what the hardware can
 * honour. Everywhere else in this file the second one wins, and it wins here.
 */
let tizenInstalledApps: Set<string> | null = null;

/** The installed-app id set, or null when we do not (yet) know. */
export function installedAppIds(): ReadonlySet<string> | null {
  return tizenInstalledApps;
}

/**
 * Ask the panel what it has. Callback-style (pre-Promise Tizen API) and needs
 * `application.info`, which config.xml declares. Called once at boot, before
 * React renders, so the answer is almost always present by the time a launcher
 * grid is built - and when it is not, nothing is filtered rather than a grid
 * being emptied by a race.
 */
export function refreshTizenInstalledApps(): void {
  try {
    const app = (
      window as unknown as {
        tizen?: {
          application?: {
            getAppsInfo?: (
              ok: (list: Array<{ id?: string }>) => void,
              err: (e: unknown) => void
            ) => void;
          };
        };
      }
    ).tizen?.application;
    if (typeof app?.getAppsInfo !== "function") return;
    // A refresh means "ask again", so the honest state until the answer lands
    // is unknown - not the previous panel's answer. Cleared only once we know
    // we can actually ask, so a call on a host with no Tizen API leaves a good
    // cache alone.
    tizenInstalledApps = null;
    app.getAppsInfo(
      (list) => {
        try {
          const ids = (Array.isArray(list) ? list : [])
            .map((a) => cleanField(a?.id))
            .filter((id): id is string => !!id);
          // An empty answer is not evidence of an empty television - it is a
          // failed read. Leaving the cache null keeps every tile visible.
          if (ids.length) tizenInstalledApps = new Set(ids);
        } catch {
          /* keep the cache unknown */
        }
      },
      () => {
        /* denied or unavailable - stays unknown, nothing is filtered */
      }
    );
  } catch {
    /* see above */
  }
}

/** Whether the Tizen B2B input API is present (drives the capability). */
function tizenInputControl(): boolean {
  return typeof tizenControl()?.getInputSourceList === "function";
}

/** Kick off an async refresh of the Tizen input cache (fire-and-forget). */
function refreshTizenInputs(): void {
  const ctl = tizenControl();
  if (typeof ctl?.getInputSourceList !== "function") return;
  try {
    ctl.getInputSourceList((list) => {
      tizenInputs = (Array.isArray(list) ? list : [])
        .map((s) =>
          toInputSource({
            id: s?.source === undefined ? undefined : String(s.source),
            label: s?.label ?? s?.type,
            connected: s?.connect,
          })
        )
        .filter((s): s is InputSource => s !== null);
    });
  } catch {
    /* leave the cache as-is */
  }
}

/** Whether Samsung hospitality firmware exposes screen-mirroring control. */
function tizenMirrorControl(): boolean {
  return typeof tizenControl()?.getScreenMirroring === "function";
}

/** Kick off an async refresh of the Tizen mirroring cache (fire-and-forget). */
function refreshTizenMirroring(): void {
  const ctl = tizenControl();
  if (typeof ctl?.getScreenMirroring !== "function") return;
  try {
    ctl.getScreenMirroring((state) => {
      tizenMirroring =
        typeof state === "boolean" ? state : state?.enable === true;
    });
  } catch {
    /* leave the cache as-is */
  }
}

/* ------------------------------------------------------------------ *
 * Checkout guest-data clear (native app logins)
 *
 * Every hospitality platform runs this clear ITSELF; the integrator's job is
 * to pull the hook at check-out. Philips gives us a named, verifiable one via
 * the shell (JEDI `IPMS.checkOutSuccessful`, gated on the firmware declaring
 * ROOM_STATUS_CHECK_OUT).
 *
 * Samsung and LG publish theirs only inside partner SDKs (Tizen hospitality /
 * LG Pro:Centric HCAP), which we do not have on file - and the entry point has
 * historically differed between firmware generations. Rather than hard-code a
 * name we cannot verify, we PROBE for a small set of documented-behaviour
 * candidates at runtime and report back the one that actually answered.
 *
 * The rule that keeps this honest: a probe that finds nothing reports
 * `cleared:false, method:"none"` and the capability stays FALSE. We never
 * assume, and because every attempt is acked to the server with its method
 * name, the fleet tells us the true entry point from real hardware instead of
 * us guessing it from a datasheet.
 * ------------------------------------------------------------------ */

/** Candidate Samsung B2B hospitality clear entry points, most-specific first. */
const TIZEN_CLEAR_CANDIDATES = [
  "clearGuestData",
  "clearUserData",
  "checkOut",
  "setCheckOut",
] as const;

/** Candidate Samsung B2B check-in entry points, most-specific first. */
const TIZEN_CHECKIN_CANDIDATES = [
  "setGuestCheckIn",
  "checkIn",
  "setCheckIn",
] as const;

/** Candidate LG HCAP check-in entry points, most-specific first. */
const HCAP_CHECKIN_CANDIDATES = [
  "checkIn",
  "setCheckIn",
  "setGuestCheckIn",
] as const;

/** Candidate LG HCAP clear entry points, most-specific first. */
const HCAP_CLEAR_CANDIDATES = [
  "clearGuestData",
  "clearUserCredentials",
  "checkOut",
] as const;

type CallbackFn = (
  onSuccess?: (res?: unknown) => void,
  onError?: (e: unknown) => void
) => void;

/**
 * Call a feature-detected, callback-style platform method and resolve with
 * whether it reported success. Resolves `false` on error, on a throw, or if the
 * platform never calls back within `timeoutMs` - a hung firmware call must not
 * hang the checkout wipe (and must not be recorded as a success).
 */
function callWithCallbacks(fn: CallbackFn, timeoutMs = 4000): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    const timer = setTimeout(() => done(false), timeoutMs);
    try {
      fn(
        () => {
          clearTimeout(timer);
          done(true);
        },
        () => {
          clearTimeout(timer);
          done(false);
        }
      );
    } catch {
      clearTimeout(timer);
      done(false);
    }
  });
}

/** Find the first callable candidate on `host`, or null. */
function findCandidate(
  host: Record<string, unknown> | undefined,
  candidates: readonly string[]
): { name: string; fn: CallbackFn } | null {
  if (!host) return null;
  for (const name of candidates) {
    const fn = host[name];
    if (typeof fn === "function") {
      return { name, fn: (fn as CallbackFn).bind(host) };
    }
  }
  return null;
}

/** The LG HCAP namespace, when the hospitality firmware injected it. */
function hcapControl(): Record<string, unknown> | undefined {
  if (typeof window === "undefined") return undefined;
  const hcap = window.hcap;
  if (!hcap) return undefined;
  // HCAP groups its API into modules; the guest/property module is where a
  // clear would live. Fall back to the root so a flatter build still probes.
  const property = hcap.property as Record<string, unknown> | undefined;
  return property ?? (hcap as Record<string, unknown>);
}

/** Whether THIS device can really run a firmware guest-data clear. */
function guestDataClearSupported(): boolean {
  const p = detectPlatform();
  if (p === "android") {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.clearGuestCredentials !== "function") return false;
    // The SAME APK runs on plain STBs - gate on the shell's own probe, which
    // asks the firmware whether it declares the PMS check-out feature.
    try {
      return native.guestDataMode?.() === "jedi-pms";
    } catch {
      return false;
    }
  }
  if (p === "tizen") {
    return (
      findCandidate(
        tizenControl() as unknown as Record<string, unknown> | undefined,
        TIZEN_CLEAR_CANDIDATES
      ) !== null
    );
  }
  if (p === "webos") {
    return findCandidate(hcapControl(), HCAP_CLEAR_CANDIDATES) !== null;
  }
  return false;
}

/** Whether THIS device can really send a firmware check-in signal. */
function guestCheckInSupported(): boolean {
  const p = detectPlatform();
  if (p === "android") {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.signalGuestCheckIn !== "function") return false;
    try {
      return native.guestCheckInMode?.() === "jedi-pms";
    } catch {
      return false;
    }
  }
  if (p === "tizen") {
    return (
      findCandidate(
        tizenControl() as unknown as Record<string, unknown> | undefined,
        TIZEN_CHECKIN_CANDIDATES
      ) !== null
    );
  }
  if (p === "webos") {
    return findCandidate(hcapControl(), HCAP_CHECKIN_CANDIDATES) !== null;
  }
  return false;
}

/**
 * The webOS Miracast luna service. Presence of `window.webOS.service.request`
 * is all we can feature-detect synchronously - the service itself only answers
 * asynchronously, so an unsupported set simply never populates the cache and
 * the TV keeps reporting the honest `enabled: false`.
 */
function webosMiracast(): WebOSService | undefined {
  return typeof window === "undefined" ? undefined : window.webOS?.service;
}

/** Kick off an async refresh of the webOS Miracast cache (fire-and-forget). */
function refreshWebosMirroring(): void {
  const svc = webosMiracast();
  if (typeof svc?.request !== "function") return;
  try {
    svc.request("luna://com.webos.service.miracast", {
      method: "getDeviceStatus",
      onSuccess: (res) => {
        const r = res as { enabled?: boolean; returnValue?: boolean };
        webosMirroring = r?.enabled === true;
      },
      onFailure: () => {
        webosMirroring = false;
      },
    });
  } catch {
    /* leave the cache as-is */
  }
}

/**
 * Ask the platform to bring its receiver up (Tizen: screen mirroring on; webOS:
 * the Miracast service). Returns true if a request was dispatched. Android goes
 * through the native shell instead - see `enableGuestCasting`.
 */
function setPlatformMirroring(on: boolean): boolean {
  const p = detectPlatform();
  if (p === "tizen") {
    const ctl = tizenControl();
    if (typeof ctl?.setScreenMirroring !== "function") return false;
    try {
      ctl.setScreenMirroring(on);
      tizenMirroring = on;
      return true;
    } catch {
      return false;
    }
  }
  if (p === "webos") {
    const svc = webosMiracast();
    if (typeof svc?.request !== "function") return false;
    try {
      svc.request("luna://com.webos.service.miracast", {
        method: "setDeviceStatus",
        parameters: { enabled: on },
      });
      webosMirroring = on;
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

/** The honest "this device cannot receive a cast" answer. */
const UNSUPPORTED_CASTING: CastingInfo = {
  supported: false,
  enabled: false,
  protocols: [],
  transport: "lan",
  launchable: false,
};

/**
 * The normalized casting model for whatever we're running on. Pure
 * feature-detection: each platform answers for itself, and anything without a
 * receiver reports `supported: false` - which is the Screen-Share page's cue to
 * show generic instructions rather than a QR nobody can use. Never throws.
 */
function readCastingInfo(): CastingInfo {
  const p = detectPlatform();
  if (p === "android") {
    const s = readGuestCastingStatus();
    // The SAME shell runs on MediaSuite TVs and plain STBs; only the TV's
    // firmware reports `supported`, which is how the box stays honest.
    if (s?.supported) return castingFromAndroid(s);
    // Not a MediaSuite TV: ask the shell what screen-sharing surface the BOX
    // itself has - an OEM mirroring app to open, or Chromecast built-in that is
    // always listening. A shell too old to answer reports nothing, and we fall
    // back to the instruction page exactly as before.
    const target = readScreenShareTarget();
    if (target?.available) return castingFromTarget(target);
    return UNSUPPORTED_CASTING;
  }
  if (p === "tizen" && tizenMirrorControl()) {
    refreshTizenMirroring(); // async; populates the cache for the next read
    return {
      supported: true,
      enabled: tizenMirroring === true,
      // Samsung hospitality panels advertise Smart View / Wireless Display,
      // both Miracast underneath, plus DIAL app-launch from the mobile apps.
      protocols: ["miracast", "dial"],
      transport: "wifi-direct",
      // Mirroring is entered from the TV's own source list, not by us popping a
      // receiver app - there is nothing for `launchCastReceiver` to launch.
      launchable: false,
    };
  }
  if (p === "webos") {
    refreshWebosMirroring(); // async; populates the cache for the next read
    // Only claim support once the Miracast service has actually answered - the
    // luna bridge exists on every webOS build, the service does not.
    if (webosMirroring === null) return UNSUPPORTED_CASTING;
    return {
      supported: true,
      enabled: webosMirroring,
      protocols: ["miracast"],
      transport: "wifi-direct",
      launchable: false,
    };
  }
  return UNSUPPORTED_CASTING;
}

function setTizenInput(id: string): boolean {
  const ctl = tizenControl();
  if (typeof ctl?.setInputSource !== "function") return false;
  try {
    ctl.setInputSource(id);
    return true;
  } catch {
    return false;
  }
}

/**
 * The bridge for the current device. Detection is per-call (not cached at
 * import) so it reflects the real environment and stays testable. Platform
 * cannot change at runtime, so this is cheap and safe.
 */
export const bridge: FoyerBridge = {
  get platform() {
    return detectPlatform();
  },
  capabilities() {
    const p = detectPlatform();
    return {
      // NATIVE app launching. A plain browser can still open an app's website
      // on a guest click (see `dispatchLaunch`), but it cannot hand the screen
      // to an installed app - and a remote "launch app" command would only be
      // swallowed by the popup blocker - so web reports false.
      launchApp: p !== "web",
      // Android note: the SAME shell runs on the Philips TV and a Philips STB,
      // so method-presence isn't enough - gate on the device actually reporting
      // ports. A box has none (JEDI returns []), so the picker stays hidden.
      switchInput:
        (p === "android" && readNativeInputs().length > 0) ||
        (p === "tizen" && tizenInputControl()),
      // Formalized across platforms: the normalized casting model answers for
      // Philips MediaSuite (JEDI ISharingSettings), Samsung Tizen (B2B screen
      // mirroring) and LG webOS (the Miracast luna service) alike. Like
      // switchInput this gates on the firmware actually reporting a receiver -
      // the SAME Android shell runs on a plain STB, which reports none.
      guestCasting: readCastingInfo().supported,
      // Only where the platform positively declares it can clear guest data.
      // A device that would silently no-op reports false, so the fleet never
      // claims a wipe it can't perform. See guestDataClearSupported().
      clearGuestCredentials: guestDataClearSupported(),
      guestCheckIn: guestCheckInSupported(),
      // Same shared-APK gate as switchInput/powerControl: a care box with no
      // webcam plugged in exposes every calling method and can still not make a
      // single call. See canVideoCall().
      videoCall: canVideoCall(),
    };
  },
  getDeviceInfo() {
    return readDeviceInfo();
  },
  appVersion() {
    return readAppVersion();
  },
  streamingAppVersion(packageSlug) {
    return readStreamingAppVersion(packageSlug);
  },
  getNetworkInfo() {
    return readNetworkInfo();
  },
  launchApp(app) {
    return dispatchLaunch(app, detectPlatform());
  },
  listInputs() {
    const p = detectPlatform();
    if (p === "android") return readNativeInputs();
    if (p === "tizen") {
      refreshTizenInputs(); // async; populates the cache for the next read
      return tizenInputs;
    }
    return [];
  },
  setInput(id) {
    const p = detectPlatform();
    if (p === "android") {
      const native = window.FoyerNative;
      if (typeof native?.setInput !== "function") return false;
      try {
        native.setInput(id);
        return true;
      } catch {
        return false;
      }
    }
    if (p === "tizen") return setTizenInput(id);
    return false;
  },
  getGuestCastingStatus() {
    if (detectPlatform() !== "android") return null;
    return readGuestCastingStatus();
  },
  getCastingInfo() {
    return readCastingInfo();
  },
  getScreenShareTarget() {
    return readScreenShareTarget();
  },
  enableGuestCasting(creds) {
    // Android/MediaSuite takes credentials (the firmware owns the SoftAP +
    // Chromecast); Tizen and webOS have no credential surface at all - turning
    // their receiver on is the whole operation.
    if (detectPlatform() !== "android") return setPlatformMirroring(true);
    const native = window.FoyerNative;
    if (typeof native?.enableGuestCasting !== "function") return false;
    const ssid = cleanField(creds?.ssid);
    const password = typeof creds?.password === "string" ? creds.password : "";
    if (!ssid) return false;
    try {
      native.enableGuestCasting(ssid, password);
      return true;
    } catch {
      return false;
    }
  },
  launchCastReceiver() {
    // Only a receiver that IS the casting UI can be launched (Philips: the Cast
    // app spins up its own Wi-Fi Direct group + QR). Tizen/webOS mirroring is
    // entered from the TV's own source list - `launchable` is false there and
    // this is a no-op rather than a broken call.
    if (detectPlatform() !== "android") return false;
    const native = window.FoyerNative;
    if (typeof native?.launchCastReceiver !== "function") return false;
    try {
      native.launchCastReceiver();
      return true;
    } catch {
      return false;
    }
  },
  disableGuestCasting() {
    if (detectPlatform() !== "android") return setPlatformMirroring(false);
    const native = window.FoyerNative;
    if (typeof native?.disableGuestCasting !== "function") return false;
    try {
      native.disableGuestCasting();
      return true;
    } catch {
      return false;
    }
  },
  /**
   * Hand the native shell a new build to self-install (Phase 4 OTA, apk kind).
   * Returns false when no native updater is present - the caller then has
   * nothing to do (the web-bundle path handles those devices via a reload).
   */
  applyUpdate(url: string): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.applyUpdate !== "function") return false;
    try {
      native.applyUpdate(url);
      return true;
    } catch {
      return false;
    }
  },
  applyFirmwareUpdate(args: {
    url: string;
    version: string;
    fileName: string;
    sizeBytes: number;
  }): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.applyFirmwareUpdate !== "function") {
      // Samsung: no Android shell; the signed widget calls the TV's own
      // updater. True means the TV ACCEPTED the request, not that it flashed -
      // adoption is still measured from the reported `firmware` field.
      const update = tizenSysMethod(TIZEN_UPDATE_FIRMWARE);
      if (!update) return false;
      watchTizenFirmwareProgress();
      try {
        console.info(
          `[firmware] tizen updateFirmware ${args.fileName} ${args.version} ${args.sizeBytes}B from ${args.url}`
        );
        update(
          TIZEN_FIRMWARE_SOFTWARE_ID,
          args.fileName,
          args.version,
          args.url,
          args.sizeBytes
        );
        return true;
      } catch (err) {
        console.info(
          `[firmware] tizen updateFirmware refused: ${err instanceof Error ? `${err.name}: ${err.message}` : String(err)}`
        );
        return false;
      }
    }
    try {
      native.applyFirmwareUpdate(
        args.url,
        args.version,
        args.fileName,
        args.sizeBytes
      );
      return true;
    } catch {
      return false;
    }
  },
  restoreClone(url: string, folder = ""): Promise<CloneRestoreResult> {
    if (!canRestoreClone()) {
      return Promise.resolve({
        applied: false,
        error: "clone restore unavailable",
      });
    }
    return restoreTizenClone(url, folder).catch((e) => ({
      applied: false,
      error: errText(e),
    }));
  },
  setPower(on: boolean): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    // Samsung: mute the panel rather than touching system power, and CONFIRM it
    // by reading the state back off the hardware. `setPanelMute` takes "is the
    // panel blanked", which is the inverse of "is the display on".
    if (typeof native?.setPower !== "function") {
      const set = tizenSysMethod(TIZEN_PANEL_SET);
      const get = tizenSysMethod(TIZEN_PANEL_GET);
      if (!set || !get) return false;
      try {
        set(!on);
        // The read-back is the whole point: this is the first platform where a
        // power switch can be verified against the hardware instead of trusted,
        // and an unverified switch would inflate the ESG savings figure - the
        // one number we must never guess at.
        return truthy(get()) === !on;
      } catch {
        return false;
      }
    }
    // The device must not claim a state change it can't make: on the shared
    // Android APK `setPower` exists on plain STBs too, where it does nothing.
    if (!canControlPower()) return false;
    try {
      // Trust the shell's verdict. `undefined` (a shell that returns nothing)
      // is NOT confirmation - an unverified switch would inflate the ESG
      // savings figure, which is the one number we must never guess at.
      return native.setPower(on) === true;
    } catch {
      return false;
    }
  },
  managementMode(): ManagementMode {
    return readManagementMode();
  },
  rebootDevice(): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    // Samsung: no Android shell, but a signed widget with `systemcontrol` can
    // restart the panel itself. Gated on the method actually existing, so a
    // firmware without it reports a refusal rather than swallowing the command.
    if (typeof native?.rebootDevice !== "function") {
      const reboot = tizenSysMethod(TIZEN_REBOOT);
      if (!reboot) return false;
      try {
        reboot();
        // Unlike the Android shell there is no verdict to trust: the call
        // either throws or the television goes down under us. Reaching the
        // next line without an exception is the strongest confirmation this
        // API offers, and the fleet console re-checks liveness by heartbeat
        // anyway - a reboot that did not happen shows up as a device that
        // never went away.
        return true;
      } catch {
        return false;
      }
    }
    // Don't even ask an un-enrolled shell: a `false` from the OS and a `false`
    // from "we were never privileged" mean the same thing to the caller, but
    // asking would log a refusal on every reboot command on every hand-held box
    // in the estate.
    if (readManagementMode() !== "device-owner") return false;
    try {
      // Trust the shell's verdict, exactly like setPower(): `undefined` from an
      // older shell is NOT confirmation. A reboot we merely asked for, reported
      // as a reboot that happened, is how a frozen TV stays frozen while the
      // console says it was recovered.
      return native.rebootDevice() === true;
    } catch {
      return false;
    }
  },
  setNtpServer(server: string): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.setNtpServer !== "function") return false;
    const value = cleanField(server);
    if (!value) return false;
    try {
      native.setNtpServer(value);
      return true;
    } catch {
      return false;
    }
  },
  unlockKioskForProSettings(): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.unlockKioskForProSettings !== "function") return false;
    try {
      native.unlockKioskForProSettings();
      return true;
    } catch {
      return false;
    }
  },
  startNativePlayback(url: string, licenceUrl?: string | null): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.startNativePlayback !== "function") return false;
    try {
      return native.startNativePlayback(url, licenceUrl ?? "") === true;
    } catch {
      return false;
    }
  },
  stopNativePlayback(): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.stopNativePlayback !== "function") return false;
    try {
      return native.stopNativePlayback() === true;
    } catch {
      return false;
    }
  },
  async clearGuestCredentials(): Promise<CredentialWipeResult> {
    const platform = detectPlatform();
    const unsupported = (detail: string): CredentialWipeResult => ({
      cleared: false,
      method: "none",
      detail,
      platform,
    });

    if (platform === "android") {
      const native =
        typeof window === "undefined" ? undefined : window.FoyerNative;
      if (typeof native?.clearGuestCredentials !== "function") {
        return unsupported("shell has no guest-data clear");
      }
      try {
        // The shell answers with the firmware's own verdict, already shaped.
        const raw = native.clearGuestCredentials();
        const parsed = raw
          ? (JSON.parse(raw) as Partial<CredentialWipeResult>)
          : null;
        if (!parsed) return unsupported("shell returned nothing");
        return {
          cleared: parsed.cleared === true,
          method: typeof parsed.method === "string" ? parsed.method : "none",
          detail: typeof parsed.detail === "string" ? parsed.detail : "",
          platform,
        };
      } catch (err) {
        return unsupported(`shell call failed: ${String(err)}`);
      }
    }

    if (platform === "tizen") {
      const found = findCandidate(
        tizenControl() as unknown as Record<string, unknown> | undefined,
        TIZEN_CLEAR_CANDIDATES
      );
      if (!found) {
        return unsupported(
          "no Samsung B2B guest-data entry point on this firmware"
        );
      }
      const ok = await callWithCallbacks(found.fn);
      return {
        cleared: ok,
        method: ok ? `tizen-b2b:${found.name}` : "none",
        detail: ok
          ? `b2bcontrol.${found.name}`
          : `b2bcontrol.${found.name} refused or timed out`,
        platform,
      };
    }

    if (platform === "webos") {
      const found = findCandidate(hcapControl(), HCAP_CLEAR_CANDIDATES);
      if (!found) {
        // Not silent failure: LG hospitality firmware clears OTT credentials at
        // power-off by default, so an unhooked set is not a wide-open one - but
        // that default is the FIRMWARE's, not ours, so we report it as such
        // rather than counting it as a wipe we performed.
        return unsupported(
          "no HCAP guest-data entry point; firmware power-off default applies"
        );
      }
      const ok = await callWithCallbacks(found.fn);
      return {
        cleared: ok,
        method: ok ? `hcap:${found.name}` : "none",
        detail: ok
          ? `hcap.${found.name}`
          : `hcap.${found.name} refused or timed out`,
        platform,
      };
    }

    return unsupported(`platform ${platform} has no guest-data clear`);
  },
  async signalGuestCheckIn(): Promise<PlatformSignalResult> {
    const platform = detectPlatform();
    const unsupported = (detail: string): PlatformSignalResult => ({
      ok: false,
      method: "none",
      detail,
      platform,
    });

    if (platform === "android") {
      const native =
        typeof window === "undefined" ? undefined : window.FoyerNative;
      if (typeof native?.signalGuestCheckIn !== "function") {
        return unsupported("shell has no check-in signal");
      }
      try {
        const raw = native.signalGuestCheckIn();
        const parsed = raw
          ? (JSON.parse(raw) as Partial<PlatformSignalResult>)
          : null;
        if (!parsed) return unsupported("shell returned nothing");
        return {
          ok: parsed.ok === true,
          method: typeof parsed.method === "string" ? parsed.method : "none",
          detail: typeof parsed.detail === "string" ? parsed.detail : "",
          platform,
        };
      } catch (err) {
        return unsupported(`shell call failed: ${String(err)}`);
      }
    }

    if (platform === "tizen") {
      const found = findCandidate(
        tizenControl() as unknown as Record<string, unknown> | undefined,
        TIZEN_CHECKIN_CANDIDATES
      );
      if (!found) {
        return unsupported(
          "no Samsung B2B check-in entry point on this firmware"
        );
      }
      const ok = await callWithCallbacks(found.fn);
      return {
        ok,
        method: ok ? `tizen-b2b:${found.name}` : "none",
        detail: ok
          ? `b2bcontrol.${found.name}`
          : `b2bcontrol.${found.name} refused or timed out`,
        platform,
      };
    }

    if (platform === "webos") {
      const found = findCandidate(hcapControl(), HCAP_CHECKIN_CANDIDATES);
      if (!found) {
        // This is the case that actively hurts the guest: without a check-in
        // hook, LG firmware keeps clearing credentials at every power-off, so
        // they re-authenticate nightly. Report it so it shows up as a real gap
        // rather than a quiet no-op.
        return unsupported(
          "no HCAP check-in entry point; firmware will keep clearing at power-off"
        );
      }
      const ok = await callWithCallbacks(found.fn);
      return {
        ok,
        method: ok ? `hcap:${found.name}` : "none",
        detail: ok
          ? `hcap.${found.name}`
          : `hcap.${found.name} refused or timed out`,
        platform,
      };
    }

    return unsupported(`platform ${platform} has no check-in signal`);
  },
  videoCallMode(): VideoCallMode | null {
    return readVideoCallMode();
  },
  startCallSession(): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.startCallSession !== "function") {
      // Plain web / CMS preview / a pre-VC-13 shell: there is no native gate to
      // arm, and the browser prompts for camera/mic on getUserMedia itself.
      // Report "ready" so the call flow proceeds rather than waiting forever for
      // a permission event that will never fire.
      return true;
    }
    try {
      // The native side returns false only when it just raised a runtime
      // permission request; anything else (true / undefined) means proceed.
      return native.startCallSession() !== false;
    } catch {
      return false;
    }
  },
  endCallSession(): void {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.endCallSession !== "function") return;
    try {
      native.endCallSession();
    } catch {
      // A shell that throws on teardown must not turn a hung-up call into an
      // unhandled rejection; the shell also closes the gate from onPause.
    }
  },
  isCallSessionActive(): boolean {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.isCallSessionActive !== "function") return false;
    try {
      return native.isCallSessionActive() === true;
    } catch {
      return false;
    }
  },
  getFcmToken(): string | null {
    const native =
      typeof window === "undefined" ? undefined : window.FoyerNative;
    if (typeof native?.getFcmToken !== "function") return null;
    try {
      return cleanField(native.getFcmToken()) ?? null;
    } catch {
      return null;
    }
  },
};

/**
 * The shell's video-call readiness verdict, normalized - or `null` when nothing
 * can answer (plain web, CMS preview, a pre-video-calling shell) or the probe
 * returned something we don't recognise. Never throws.
 */
function readVideoCallMode(): VideoCallMode | null {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.videoCallMode !== "function") return null;
  let raw: string | null | undefined;
  try {
    raw = native.videoCallMode();
  } catch {
    // A throwing probe means the camera stack is unhealthy, not that we know
    // anything about it - report "we can't tell" rather than inventing "none".
    return null;
  }
  const mode = cleanField(raw);
  switch (mode) {
    case "camera2":
    case "no-camera":
    case "no-permission":
    case "none":
      return mode;
    default:
      return null;
  }
}

/**
 * True when this device could carry a video call RIGHT NOW.
 *
 * Deliberately as strict as {@link canControlPower}, and for the same reason:
 * one Android APK ships to Philips MediaSuite TVs, care-room STBs with a USB
 * webcam, and identical boxes with nothing plugged in at all. Method presence
 * says only that the build has calling compiled in; it cannot distinguish the
 * room that can call from the room whose camera a resident unplugged last
 * Tuesday. So the capability is gated on the shell's own probe having actually
 * enumerated a camera AND the OS having granted capture - anything less
 * (`no-camera`, `no-permission`, `none`, an absent or throwing probe) is false.
 *
 * The cost of being wrong is asymmetric and points the same way as ESG: a
 * falsely-advertised call rings a family's phone and then fails in a resident's
 * bedroom, which is worse than a room that never offered the button.
 */
export function canVideoCall(): boolean {
  return readVideoCallMode() === "camera2";
}

/**
 * True when the shell can REALLY control display power (Phase 5 - scheduled
 * on/off).
 *
 * The method's presence is not enough: the Android shell is ONE APK shared by
 * MediaSuite TVs and plain STBs, so a box would expose `setPower` and silently
 * do nothing - which is exactly how scheduled on/off became a no-op that still
 * reported energy savings. So we require the `powerControlMode()` probe to
 * confirm a real backend, and a shell that lacks the probe is treated as
 * incapable rather than taken at its word. Deliberately stricter than
 * {@link canApplyFirmware}: a wrongly-claimed firmware trigger wastes a
 * rollout, a wrongly-claimed power switch produces ESG figures a customer may
 * publish.
 */
export function canControlPower(): boolean {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  // Samsung answered the question TIZEN-13 was holding this back for. The panel
  // reported `setPanelMute`/`getPanelMute`, which blank the backlight while the
  // system, the network and Foyer itself stay up - the QUICK_CLOCK analogue,
  // and a state we cannot be stranded in because the app that entered it is
  // still running to leave it. BOTH halves are required: without the getter we
  // could not confirm the switch against the hardware, and an unconfirmed
  // switch must not be counted as an energy saving.
  if (typeof native?.setPower !== "function") {
    return (
      tizenSysMethod(TIZEN_PANEL_SET) !== null &&
      tizenSysMethod(TIZEN_PANEL_GET) !== null
    );
  }
  if (typeof native.powerControlMode !== "function") return false;
  try {
    return native.powerControlMode() === "jedi";
  } catch {
    return false;
  }
}

/**
 * How this box is managed, from the shell's `managementMode()` probe.
 *
 * A shell that doesn't expose the probe is `"none"`, NOT "unknown": every shell
 * before kiosk 0.2.35 predates enrolment entirely, so it cannot be a device
 * owner, and an honest "no" beats a third state the fleet console would have to
 * render as a shrug. Anything unrecognised is also `"none"` - a privilege we
 * cannot name is a privilege we must not claim.
 */
function readManagementMode(): ManagementMode {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.managementMode !== "function") return "none";
  try {
    const raw = cleanField(native.managementMode());
    if (raw === "device-owner" || raw === "profile-owner") return raw;
    return "none";
  } catch {
    return "none";
  }
}

/**
 * True when the shell can reboot the DEVICE rather than reload the page.
 *
 * Both halves are required: the method must exist AND the box must actually
 * hold device-owner privilege, because the same APK runs on enrolled and
 * hand-held units and `rebootDevice` is present on both. Advertising it on a
 * box that would refuse turns "reboot room 212" into a silent no-op - the exact
 * shape of the bug this whole ticket exists to remove.
 */
function canRebootDevice(): boolean {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  // Samsung's equivalent of device-owner enrolment is running inside the signed
  // widget at all: an unprivileged browser tab on the same panel has no
  // `webapis.systemcontrol` to find, so presence IS the privilege check.
  if (typeof native?.rebootDevice !== "function") {
    return tizenSysMethod(TIZEN_REBOOT) !== null;
  }
  return readManagementMode() === "device-owner";
}

/**
 * Which native framebuffer route this device has, or `"none"`.
 *
 * Exported because it is not only a capability flag: the diagnostics capture path
 * uses it to decide whether to await a native grab at all, and the caption the
 * fleet console prints under a screenshot is derived from the same answer.
 *
 * BOTH halves are required, exactly as for {@link canRebootDevice}: the shell must
 * expose the request method AND report a route. The same APK runs on an
 * Android-Q Philips MediaSuite TV (no accessibility screenshot API) and on an STB
 * nobody enabled the capture service on, and `requestScreenshot` is present on
 * both. Advertising a framebuffer grab there would hand support a picture of our
 * own DOM captioned as the real screen - worse than no screenshot, because it
 * looks like an answer.
 *
 * A shell exposing only the LEGACY synchronous `captureScreenshot` is taken at its
 * word (`"legacy"`): it predates the mode probe, and the sync method is not one of
 * ours to have shipped without meaning it.
 */
export function nativeScreenshotMode(): string {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.requestScreenshot === "function") {
    if (typeof native.screenshotMode !== "function") return "none";
    try {
      const mode = cleanField(native.screenshotMode());
      return mode && mode !== "none" ? mode : "none";
    } catch {
      // A throwing probe means the capture layer is unhealthy, not that the box
      // has a route we can trust. Fall back to the in-app render and say so.
      return "none";
    }
  }
  if (typeof native?.captureScreenshot === "function") return "legacy";
  // Samsung's privileged framebuffer grab. Reported as its own mode rather than
  // folded into "legacy" because the console captions a screenshot from the
  // route that produced it, and "the real screen, whatever app was on it" is
  // only true here if the call really is a framebuffer read.
  if (tizenSysMethod(TIZEN_CAPTURE)) return "tizen";
  return "none";
}

/**
 * Samsung framebuffer grab, as base64, or null.
 *
 * The return contract is the part we have NOT been able to confirm off a
 * datasheet - different SSSP generations have answered with a base64 string,
 * with a file path, and through a callback. So this accepts all three shapes
 * and returns a string only when it holds something that actually looks like
 * image data. Anything else returns null and {@link captureScreenshot} falls
 * through to the DOM render, which is the behaviour a Samsung panel has today.
 *
 * That fallback is why wiring this before confirming the signature is safe:
 * the worst case is the picture we already take, correctly captioned "in-app
 * view only" - never a DOM render mislabelled as the real screen.
 */
export async function captureTizenScreenshot(): Promise<string | null> {
  const grab = tizenSysMethod(TIZEN_CAPTURE);
  if (!grab) return null;
  let out: unknown;
  try {
    out = grab();
  } catch {
    return null;
  }
  if (typeof out !== "string" || out.length === 0) return null;

  // Already image data - the shape the datasheet implies, and the shape some
  // generations really do return.
  if (out.length > 64 && !looksLikeFilePath(out)) return out;

  // CONFIRMED on a QB24C: it answers with a PATH (`/tmp/ScreenCapture.jpg`),
  // having written the framebuffer there itself. Short, so the length guard
  // above used to reject it and every Samsung screenshot quietly fell through
  // to the DOM render - correctly captioned, and never the real screen.
  if (looksLikeFilePath(out)) {
    const read = await readTizenFileBase64(out);
    if (read) return read;
    console.warn(
      `[tizen] captureScreen wrote ${out} but it could not be read back`
    );
    return null;
  }

  // Neither. Say what it was, because the return contract differs by
  // generation and the next panel that surprises us should cost one log line
  // rather than another afternoon.
  console.warn(
    `[tizen] captureScreen returned an unrecognised ${out.length}-char value`
  );
  return null;
}

/**
 * Does this look like somewhere on disk rather than image data?
 *
 * Both forms appear: a bare POSIX path and a `file://` URI. Base64 never
 * contains `/` at the start nor a dot-extension at the end, so the test is
 * cheap and does not need to be clever.
 */
function looksLikeFilePath(value: string): boolean {
  return (
    /^file:\/\//i.test(value) ||
    (value.startsWith("/") && /\.(jpe?g|png|bmp)$/i.test(value))
  );
}

/**
 * Read a file the firmware wrote, through the WGT's filesystem bridge.
 *
 * The bridge is in the widget rather than here because `tizen.filesystem` is
 * only reachable from the privileged widget context - see `tizen/index.html`.
 * Absent on an older WGT, which simply means no Samsung screenshot on that
 * panel until it updates itself.
 */
async function readTizenFileBase64(path: string): Promise<string | null> {
  const read = (
    window as unknown as {
      foyerReadFileBase64?: (p: string) => Promise<string>;
    }
  ).foyerReadFileBase64;
  if (typeof read !== "function") return null;
  try {
    const data = await read(path);
    return typeof data === "string" && data.length > 64 ? data : null;
  } catch (e) {
    console.warn(
      `[tizen] could not read ${path}: ${e instanceof Error ? e.message : String(e)}`
    );
    return null;
  }
}

/** True when this device can deliver a real framebuffer screenshot. */
function canCaptureNativeScreenshot(): boolean {
  return nativeScreenshotMode() !== "none";
}

/**
 * Which native log route this device has, or `"none"`. Same two-halves rule as
 * {@link nativeScreenshotMode}; a shell with only the legacy sync `captureLogs`
 * reports `"legacy"`.
 */
export function nativeLogMode(): string {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.requestLogs === "function") {
    if (typeof native.logCaptureMode !== "function") return "none";
    try {
      const mode = cleanField(native.logCaptureMode());
      return mode && mode !== "none" ? mode : "none";
    } catch {
      return "none";
    }
  }
  if (typeof native?.captureLogs === "function") return "legacy";
  return "none";
}

/** True when this device can deliver a native log tail (not just the console). */
function canCaptureNativeLogs(): boolean {
  return nativeLogMode() !== "none";
}

/** True when the shell can set the NTP time server (OTA add-on - `set_ntp`). */
function canSetNtp(): boolean {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  return typeof native?.setNtpServer === "function";
}

/**
 * How the native shell installs an APK (Phase 4 OTA, apk kind):
 *   "silent" - privileged/device-owner: unattended, the only mode a guest-room
 *              TV can complete on its own,
 *   "prompt" - it downloads, but someone must confirm the system dialog,
 *   "none"   - no usable installer (or no native updater at all).
 * A shell that exposes `applyUpdate` but not `otaInstallMode` predates the mode
 * probe; read it as "prompt" rather than over-promising an unattended install.
 */
function apkInstallMode(): "silent" | "prompt" | "none" {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.applyUpdate !== "function") return "none";
  if (typeof native.otaInstallMode !== "function") return "prompt";
  try {
    const mode = native.otaInstallMode();
    return mode === "silent" || mode === "none" ? mode : "prompt";
  } catch {
    return "prompt";
  }
}

/**
 * True when the TV can actually flash OEM firmware from software (Philips JEDI
 * clone-in of MAIN_SOFTWARE / Tizen systemcontrol).
 *
 * The Android shell is ONE APK shared by MediaSuite TVs and plain STBs, so the
 * method's presence isn't enough - a box would expose it and silently do
 * nothing. When the shell offers the `firmwareUpdateMode()` probe we trust it
 * ("none" = no trigger); a shell that only exposes the trigger predates the
 * probe and is taken at its word.
 */
function canApplyFirmware(): boolean {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  // Samsung: the signed widget's own `systemcontrol.updateFirmware` (no shell).
  if (typeof native?.applyFirmwareUpdate !== "function") {
    return tizenSysMethod(TIZEN_UPDATE_FIRMWARE) !== null;
  }
  if (typeof native.firmwareUpdateMode !== "function") return true;
  try {
    return native.firmwareUpdateMode() !== "none";
  } catch {
    // A throwing probe means the firmware layer is unhealthy, not that the TV
    // lacks an updater - stay conservative and don't offer firmware waves.
    return false;
  }
}

/**
 * The best model string for the current device: the exact native/JEDI model
 * when the shell provides it, else the UA-derived `guessModel()` fallback.
 * Use this everywhere a model is reported (pairing, heartbeat) so an exact
 * commercial type number wins whenever it's available.
 */
export function deviceModel(): string {
  return bridge.getDeviceInfo()?.model || guessModel();
}

/**
 * Is this Android device a Philips MediaSuite TV (vs a plain set-top box)? The
 * SAME native shell runs on both, so we identify the TV by either the shell's
 * finer `platform` tag (e.g. "android-mediasuite") or - failing that - the
 * firmware actually reporting a JEDI-backed TV feature a box never has (external
 * inputs or guest casting). A plain box reports neither.
 */
function isPhilipsMediaSuite(): boolean {
  const tag = readNativePlatformTag()?.toLowerCase();
  if (tag && (tag.includes("mediasuite") || tag.includes("philips"))) {
    return true;
  }
  const caps = bridge.capabilities();
  return caps.switchInput || caps.guestCasting;
}

/** The shell's finer platform tag (`platform()`), or undefined; never throws. */
function readNativePlatformTag(): string | undefined {
  const native = typeof window === "undefined" ? undefined : window.FoyerNative;
  if (typeof native?.platform !== "function") return undefined;
  try {
    return cleanField(native.platform());
  } catch {
    return undefined;
  }
}

/**
 * Classify the concrete device family (see {@link DevicePlatform}). Maps the
 * TV-side {@link TvPlatform} through unchanged for tizen/webos/web, and splits
 * Android into `philips` vs `stb` via {@link isPhilipsMediaSuite}. Used for the
 * fleet console's platform filter and capability-gated features.
 */
export function devicePlatform(): DevicePlatform {
  const p = detectPlatform();
  if (p !== "android") return p; // tizen | webos | web pass straight through
  return isPhilipsMediaSuite() ? "philips" : "stb";
}

/**
 * The capabilities this device can perform right now, as a serializable list for
 * the heartbeat / fleet console. Derived from {@link FoyerBridge.capabilities};
 * only currently-implemented capabilities are emitted (screenshot/OTA land in a
 * later increment).
 */
export function deviceCapabilities(): DeviceCapability[] {
  const c = bridge.capabilities();
  const out: DeviceCapability[] = [];
  if (c.launchApp) out.push("launchApp");
  if (c.switchInput) out.push("switchInput");
  if (c.guestCasting) out.push("guestCasting");
  // In-app screenshot (DOM capture) + a console log ring buffer work on every
  // web-rendered platform, so they're always offered.
  out.push("screenshot");
  out.push("logCapture");
  // …and the FIDELITY of each is reported separately, because it is a different
  // question with a different answer per box. `screenshot` alone only ever meant
  // "we can render our own React tree", which is a picture of a healthy TV even
  // when the panel is black, the renderer has crashed, a firmware dialog is up, or
  // the Norigin app is in front - i.e. in every state support asks for one.
  // `screenshotNative` is the flag that says the image can be trusted as the
  // screen; without it the console captions the capture "in-app view only".
  if (canCaptureNativeScreenshot()) out.push("screenshotNative");
  // Same split for logs: the console ring buffer lives in the page and dies with
  // it, so it can never contain the crash that killed it. `logCaptureNative` means
  // the upload also carries a logcat tail of this app's own uid - which spans
  // process lifetimes - plus why the last process exited.
  if (canCaptureNativeLogs()) out.push("logCaptureNative");
  // Every web-rendered platform can apply a web-bundle OTA (a hard reload onto
  // the freshly deployed bundle); only a shell with a native updater can
  // self-install a new APK.
  out.push("otaWebBundle");
  const apkMode = apkInstallMode();
  if (apkMode !== "none") out.push("otaApk");
  // Only a privileged/device-owner shell installs unattended; without this the
  // fleet console warns that the wave needs someone in the room to confirm.
  if (apkMode === "silent") out.push("otaApkSilent");
  // The native CONTAINER on a platform where we cannot push an update to it.
  //
  // A Samsung panel updates its widget by re-reading `sssp_config.xml` and
  // deciding for itself; there is no command we can send, so `otaApk` would be
  // a lie in both directions - the console would offer a push that does nothing
  // AND a Tizen panel with a stale widget would look like it had no container
  // at all. `otaPlatformApp` is the honest third answer: this device HAS a
  // native container and reports its version, and the way it is updated is not
  // a wave we drive. Reserved for exactly this since the type was written
  // (shared/types/foyer.ts: "Tizen/webOS native container").
  if (readAppVersion() !== null && apkMode === "none") {
    out.push("otaPlatformApp");
  }
  // OEM firmware update only where the shell exposes a native trigger (Philips
  // JEDI clone-in / Tizen systemcontrol); everyone else is "managed externally".
  if (canApplyFirmware()) out.push("otaFirmware");
  if (canRestoreClone()) out.push("cloneRestore");
  if (canControlPower()) out.push("powerControl");
  // NTP server config only where the shell exposes a native setter (Android
  // systemProperties / Settings.Global.NTP_SERVER); everyone else is unmanaged.
  if (canSetNtp()) out.push("setNtp");
  // The privilege behind three of the capabilities above. Reported on its own
  // so the fleet can answer "which boxes are provisioned, and which were
  // granted by hand on a bench?" - the two are indistinguishable from a desk
  // until an unattended rollout stalls in a resident's room.
  if (readManagementMode() === "device-owner") out.push("deviceOwner");
  // A real device reboot (the only thing that recovers a wedged WebView).
  // Requires BOTH the shell method and the privilege - see bridge.rebootDevice.
  if (canRebootDevice()) out.push("rebootDevice");
  // Reported so the fleet can answer "which of my TVs can actually wipe a
  // guest's app logins at checkout?" from device truth rather than a spec sheet.
  if (c.clearGuestCredentials) out.push("clearGuestCredentials");
  if (c.guestCheckIn) out.push("guestCheckIn");
  // Rides the heartbeat so the fleet view can answer "which rooms are
  // call-capable?" - and, when one isn't, whether the camera is even
  // enumerated - without anyone walking to the room. See canVideoCall().
  if (c.videoCall) out.push("videoCall");
  return out;
}
